const pt = {
  "swap-crypto-sans-frais": {
    title: "Swap cripto sem taxas adicionais: o que a Lumina não adiciona e o que realmente compõe o custo",
    meta: "A Lumina não adiciona as suas próprias taxas de serviço adicionais ao seu swap. Descubra as seis componentes que realmente formam o custo de um swap: comissão comercial paga pelo parceiro à Lumina, margem do parceiro, taxas de rede, spread, slippage e impacto de preço.",
    body: `Às vezes você vê a expressão "swap cripto sem taxas". Esta formulação pode ser enganosa: descreve a ausência de taxas de serviço adicionais aplicadas pela wallet, não a ausência de todo custo. Um swap cripto continua sendo uma operação de mercado com vários componentes de custo, independentes da Lumina.

## O resumo sem ambiguidade

A Lumina não adiciona as suas próprias taxas de serviço adicionais ao seu swap. A Lumina pode receber uma comissão comercial do seu parceiro de câmbio. A cotação pode incluir as condições tarifárias do parceiro, o spread, o slippage, o impacto de preço e as taxas de rede. A taxa e o valor estimado a receber são exibidos antes da sua confirmação.

## As seis componentes do custo de um swap

Para entender o que você realmente paga, é preciso distinguir seis elementos:

### 1. As taxas de serviço adicionadas pela Lumina

A Lumina **não adiciona** taxas de serviço à cotação fornecida pelo parceiro de câmbio. O valor apresentado a você já reflete esta ausência de sobretaxa.

### 2. A comissão comercial recebida pela Lumina

A Lumina pode receber uma comissão comercial do seu parceiro de câmbio. Esta comissão não é adicionada pela Lumina como uma linha de taxa separada acima da cotação. Pode, no entanto, refletir-se nas condições tarifárias propostas pelo parceiro.

### 3. As taxas ou a margem do parceiro de câmbio

O parceiro de câmbio pode aplicar as suas próprias taxas operacionais e a sua própria margem. Estes elementos são incorporados na taxa e no valor estimado exibidos na cotação antes da sua confirmação.

### 4. As taxas de rede (gas fees)

As taxas de rede remuneram os mineradores ou validadores que processam a transação. Variam de acordo com a blockchain e a sua congestão. Dependendo do fluxo de swap utilizado, podem ser estimadas, agrupadas ou apresentadas na cotação do parceiro.

### 5. O spread

O spread é a diferença entre o preço de compra e o preço de venda exibido pela fonte de liquidez. É um custo implícito: você não o vê como uma linha de taxa, mas influencia diretamente a taxa aplicada ao seu swap.

### 6. O slippage e o impacto de preço

- **O slippage** é a diferença entre a taxa estimada no momento da cotação e a taxa realmente obtida na execução. Aumenta quando a liquidez disponível diminui.
- **O impacto de preço** é o efeito que a sua própria ordem tem sobre o preço: quanto maior for o seu valor em relação à liquidez, pior será o preço para você.

Estes dois elementos podem modificar o valor final que você recebe em relação à estimativa inicial.

## O que a Lumina exibe antes da sua confirmação

Antes da sua confirmação, a Lumina exibe a taxa e o valor estimados comunicados pelo parceiro, bem como as informações tarifárias disponíveis na cotação. As taxas de rede são exibidas quando são indicadas ou estimadas pelo fluxo de swap correspondente.

Você mantém a decisão final: após consultar a cotação, você escolhe confirmar ou cancelar a operação.

## Como fazer um swap na Lumina Wallet

1. Abra a aba **Swap** no aplicativo.
2. Escolha o token que quer trocar e o token a receber.
3. Insira o valor a trocar.
4. Consulte a cotação exibida: taxa e valor estimados, bem como as informações tarifárias disponíveis (incluindo as taxas de rede quando são indicadas ou estimadas).
5. Se a cotação lhe convém, confirme a transação. Caso contrário, cancele e ajuste.

## Para lembrar

Um swap cripto nunca é totalmente gratuito, mesmo quando a própria wallet não adiciona taxas de serviço. O custo real depende do parceiro, da rede, do spread, do slippage e do impacto de preço. A boa prática consiste em examinar a cotação, comparar se necessário e verificar cada parâmetro antes de confirmar.

**Pronto para experimentar?** [Baixe a Lumina Wallet](/pt/download) e faça o seu primeiro swap mantendo o controle em cada etapa.`
  },
  "comment-eviter-arnaques-crypto": {
    title: "Como Evitar Golpes Crypto em 2026: O Guia Definitivo",
    meta: "Descubra os golpes crypto mais comuns em 2026 (phishing, rug pull, falsos airdrops) e aprenda a evitá-los para proteger seus ativos. O guia completo para proteger sua carteira.",
    body: `O mundo das criptomoedas é fascinante, mas também atrai muitos agentes maliciosos. Em 2025, os golpes crypto atingiram um nível de sofisticação sem precedentes, custando bilhões de dólares aos investidores [1]. Compreender essas ameaças é o primeiro passo para proteger seus ativos digitais. Este guia explicará os golpes mais comuns e como se proteger, principalmente com ferramentas inovadoras como o scanner anti-golpes da Lumina Wallet.

## Os Tipos de Golpes Crypto Mais Comuns em 2026

### 1. Phishing

O phishing continua sendo o método mais utilizado. Golpistas criam sites falsos que imitam perfeitamente plataformas legítimas para te induzir a inserir sua frase de recuperação (seed phrase) ou conectar sua carteira.

> **Exemplo concreto**: Você recebe um email aparentemente vindo do MetaMask ou da sua exchange, pedindo para verificar sua conta clicando em um link. Esse link redireciona para um site clone que registrará suas informações confidenciais.

**Como evitar**:
- Nunca clique em links de emails ou mensagens não solicitadas.
- Verifique sempre a URL do site.
- **NUNCA** insira sua frase de recuperação em um site web.

### 2. Rug Pulls

Um "rug pull" ocorre quando uma equipe de desenvolvedores lança um novo token, atrai investidores para fazer o preço subir e depois retira repentinamente toda a liquidez do projeto.

**Como evitar**:
- **Pesquise a equipe**: Uma equipe anônima é um grande sinal de alerta.
- **Verifique a liquidez**: Está bloqueada? Plataformas como UniCrypt permitem verificar.
- **Analise a distribuição de tokens**: Poucas carteiras com muitos tokens = alto risco.

### 3. Falsos Airdrops e Giveaways

Golpistas prometem tokens gratuitos em troca de uma "taxa de transação" ou pedindo para conectar sua carteira a um site malicioso.

**Como evitar**:
- Airdrops legítimos **nunca** pedem dinheiro ou sua frase de recuperação.
- Desconfie de promessas irrealistas.
- Verifique nos canais oficiais do projeto.

### 4. Golpes de Aprovação de Smart Contracts (Approve/Permit)

Você assina uma transação que dá a um smart contract malicioso permissão para gastar todos os tokens da sua carteira.

## Como a Lumina Wallet Te Protege

| Funcionalidade de Segurança | Como a Lumina Te Protege |
|---|---|
| **Scanner Anti-Golpes em Tempo Real** | Antes de cada transação, analisa endereços e smart contracts para detectar ameaças. |
| **Simulação de Transação** | Mostra exatamente o que a transação fará **antes** de você assinar. |
| **Auto-custódia (Non-Custodial)** | Suas chaves privadas nunca saem do seu dispositivo. |

## Conclusão

Usando uma carteira segura como **Lumina Wallet**, você adiciona uma camada essencial de proteção tecnológica.

**Pronto para proteger suas cryptos?** [Baixe a Lumina Wallet hoje](#) e opere com total tranquilidade.

---

### Referências

[1] Chainalysis. (2026). *2026 Crypto Crime Report: Scams*. [https://www.chainalysis.com/blog/crypto-scams-2026/](https://www.chainalysis.com/blog/crypto-scams-2026/)`
  },
  "securiser-wallet-crypto": {
    title: "Como Proteger Sua Carteira Crypto: 10 Passos Essenciais",
    meta: "Aprenda a proteger sua carteira crypto com nosso guia completo. Proteja sua frase de recuperação, use uma carteira non-custodial e ative o 2FA.",
    body: `A segurança das suas criptomoedas depende não apenas da plataforma que você usa, mas também dos seus próprios hábitos.

## 1. Escolha uma Carteira Non-Custodial

Uma carteira **non-custodial** como **Lumina Wallet** significa que só você possui as chaves privadas dos seus ativos.

> **"Not your keys, not your coins"** é o mantra.

## 2. Proteja Sua Frase de Recuperação (Seed Phrase)

- **Escreva em papel**: Nunca armazene em dispositivo conectado à Internet.
- **Faça várias cópias**: Guarde em locais diferentes e seguros.
- **NUNCA compartilhe**.

## 3. Use uma Senha Forte e Única

## 4. Ative a Autenticação de Dois Fatores (2FA)

## 5. Cuidado com Wi-Fi Público

## 6. Separe Seus Fundos: Carteira "Quente" e "Fria"

| Tipo de Carteira | Uso | Exemplo |
|---|---|---|
| **Carteira Quente (Hot Wallet)** | Transações diárias, swaps. | **Lumina Wallet** (mobile/web) |
| **Carteira Fria (Cold Wallet)** | Armazenamento de longo prazo. | Hardware wallet (Ledger, Trezor) |

## 7. Mantenha Seu App Atualizado

## 8. Seja Cético com dApps e Novos Projetos

Use uma carteira de teste. **Lumina Wallet** ajuda simulando transações.

## 9. Revogue Aprovações

Use [Revoke.cash](https://revoke.cash/) para verificar e revogar permissões desnecessárias.

## 10. Use uma Carteira com Recursos de Segurança Integrados

**Lumina Wallet** integra scanner anti-golpes, simulação de transação e suporte multi-chain nativo.

## Conclusão

Seguindo estes 10 passos com **Lumina Wallet**, você reduz significativamente os riscos.

**Pronto para o próximo nível de segurança?** [Baixe a Lumina Wallet](#) e assuma o controle total.`
  },
"wallet-non-custodial-guide": {
    title: "O Que é uma Carteira Non-Custodial? O Guia Completo",
    meta: "Entenda a diferença entre uma carteira custodial e non-custodial (auto-custódia). Descubra por que uma carteira non-custodial como Lumina Wallet é essencial para a segurança das suas criptomoedas.",
    body: `Se você é novo no mundo das criptomoedas, provavelmente já ouviu os termos "custodial" e "non-custodial". Entender essa distinção é **o mais importante** para a segurança dos seus ativos.

## Custodial vs. Non-Custodial: A Diferença-Chave

| Tipo de Carteira | Quem controla as chaves privadas? | Exemplos |
|---|---|---|
| **Custodial** | A plataforma (terceiro) | Exchanges (Binance, Coinbase, Kraken) |
| **Non-Custodial (Auto-custódia)** | **Você** | **Lumina Wallet**, MetaMask, Ledger, Trezor |

### Custodial: Simplicidade ao Custo do Controle

Se a plataforma for hackeada, você pode perder tudo. **"Not your keys, not your coins."**

### Non-Custodial: Controle Total e Verdadeira Propriedade

**Lumina Wallet** te dá controle total com uma **frase de recuperação** de 12 ou 24 palavras.

**Vantagens:**
- **Controle Total**: Você é seu próprio banco.
- **Segurança Máxima**: Sem risco de hack da plataforma.
- **Privacidade**: Sem KYC necessário.

## Lumina Wallet: O Melhor dos Dois Mundos

- **100% Non-Custodial**
- **Interface Intuitiva**
- **Multi-chain**: Gerencie todos os ativos em um único app.

**Pronto para ser seu próprio banco?** [Baixe a Lumina Wallet hoje](#) e descubra a verdadeira propriedade crypto.`
  },
  "comparatif-wallets-crypto": {
    title: "Comparativo das Melhores Carteiras Crypto em 2026",
    meta: "Qual é a melhor carteira crypto em 2026? Comparativo completo entre Lumina Wallet, MetaMask, Trust Wallet e hardware wallets (Ledger, Trezor).",
    body: `Escolher a carteira crypto certa é crucial. Este comparativo analisa as carteiras mais populares em 2026.

## O Comparativo: Lumina vs. MetaMask vs. Trust Wallet vs. Ledger

| Funcionalidade | Lumina Wallet | MetaMask | Trust Wallet | Ledger (Hardware) |
| --- | --- | --- | --- | --- |
| **Tipo** | Non-Custodial | Non-Custodial | Non-Custodial | Non-Custodial (Frio) |
| **Segurança** | Muito Alta (Scanner anti-golpes) | Alta | Alta | Máxima (Offline) |
| **Suporte Multi-chain** | BTC, ETH, SOL, XRP +10 | Redes EVM, Solana e Bitcoin | Bom | Bom (Ledger Live) |
| **Facilidade de Uso** | Pensada para iniciantes | Mais técnica para iniciantes | Fácil | Média |
| **Swap Integrado** | Sim (sem taxas de serviço adicionais da Lumina; podem aplicar-se preços do parceiro, taxas de rede, spread, slippage e impacto no preço) | Sim (0,875%) | Sim | Sim (com taxas) |
| **Galeria NFT** | Sim (Multi-chain) | Sim (EVM) | Sim | Sim |
| **Plataformas** | iOS, Android, Web | Extensão, Mobile | Mobile | Dispositivo + Desktop/Mobile |

## Conclusão

- Para **gestão diária**: **Lumina Wallet**
- Para **armazenamento de longo prazo**: **Hardware wallet**
- Para **especialistas DeFi Ethereum**: **MetaMask**

**Pronto para escolher?** [Baixe a Lumina Wallet](#) e descubra uma nova experiência crypto.`
  },
  "guide-debutant-crypto": {
    title: "Guia para Iniciantes: Como Começar em Crypto em 2026",
    meta: "Quer começar em crypto mas não sabe por onde? Este guia simples explica tudo: comprar suas primeiras cryptos, escolher uma carteira segura e fazer suas primeiras transações.",
    body: `O mundo das criptomoedas pode parecer intimidador no início. Porém, começar é mais simples do que parece.

## Passo 1: Entender os Conceitos Básicos

- **Blockchain**: Um registro digital público, descentralizado e seguro.
- **Criptomoeda**: Um ativo digital que usa criptografia. Os mais conhecidos: Bitcoin (BTC) e Ethereum (ETH).
- **Carteira (Wallet)**: Uma aplicação para armazenar, enviar e receber criptomoedas.

## Passo 2: Comprar Suas Primeiras Criptomoedas

Através de uma **exchange centralizada (CEX)** como Coinbase, Binance ou Kraken.

1. **Crie uma conta**.
2. **Verifique sua identidade** (KYC).
3. **Deposite fundos**.
4. **Compre a criptomoeda** de sua escolha.

## Passo 3: Proteger Suas Cryptos em uma Carteira Non-Custodial

É **crucial transferir** suas cryptos para uma carteira que você controle totalmente, como **Lumina Wallet**.

1. **Baixe a Lumina Wallet**.
2. **Crie uma nova carteira**. O app gerará uma **frase de recuperação**.
3. **Anote essa frase em papel** e guarde em local seguro.
4. **Transfira suas cryptos** para seu novo endereço Lumina Wallet.

## Passo 4: Suas Primeiras Transações

- **Enviar e Receber**: Envie cryptos usando o endereço da carteira.
- **Swap**: Troque criptomoedas diretamente na Lumina Wallet, sem taxas de serviço adicionais da Lumina (as taxas de rede e as condições do parceiro continuam a aplicar-se).
- **Interagir com dApps**: Via WalletConnect, conecte-se a milhares de aplicativos descentralizados.

## Dicas para Iniciantes

- **Invista apenas o que está disposto a perder**.
- **Faça sua própria pesquisa (DYOR)**.
- **Tenha paciência**.
- **Segurança em primeiro lugar**.

**Pronto para seus primeiros passos?** [Baixe a Lumina Wallet](#) e junte-se à revolução das finanças descentralizadas.`
  },
  "seed-phrase-guide": {
    title: "O Que é a Frase de Recuperação (Seed Phrase)?",
    meta: "Sua frase de recuperação (seed phrase) é a chave da sua carteira crypto. Aprenda o que é, como funciona e as melhores práticas para armazená-la com segurança.",
    body: `No mundo das carteiras non-custodial, a **frase de recuperação** (também chamada de *seed phrase*) é o conceito de segurança mais fundamental.

## O Que é uma Frase de Recuperação?

Uma lista de **12 a 24 palavras** gerada ao criar uma carteira crypto non-custodial como **Lumina Wallet**.

> **Exemplo**: \`witch collapse practice feed shame open despair creator tenant camp strike\`

## Como Funciona?

Baseada no padrão criptográfico **BIP-39**:

1. A carteira gera uma sequência de números aleatórios.
2. Convertida em uma lista de palavras de um dicionário padronizado de 2048 palavras.
3. A carteira deriva uma **chave privada mestra**.
4. A partir dela, gera **chaves privadas individuais** para cada criptomoeda.

## Por Que é Tão Importante?

- **Se perder o dispositivo**: Baixe Lumina Wallet em outro, insira a frase e recupere o acesso.
- **Se alguém obtiver a frase**: Pode roubar toda sua carteira.

> **Regra de ouro**: Trate sua frase de recuperação como um lingote de ouro.

## Como Armazenar com Segurança

### O que FAZER
- **Escreva em papel**.
- **Faça várias cópias** em locais seguros diferentes.
- **Use materiais duráveis**: Gravação em placa metálica.

### O que NÃO FAZER
- **NÃO armazene digitalmente**.
- **NÃO salve na nuvem**.
- **NUNCA compartilhe** com ninguém.

**Pronto para assumir o controle?** [Baixe a Lumina Wallet](#) e descubra o poder da auto-custódia.`
  },
  "arnaques-smart-contracts": {
    title: "Smart Contracts: Como Evitar Golpes de Aprovação (Approve / Permit)",
    meta: "Os golpes de smart contracts (Approve, Permit) são cada vez mais comuns. Aprenda como hackers esvaziam sua carteira e como o scanner da Lumina Wallet pode te proteger.",
    body: `Acha que está seguro porque nunca compartilha sua frase de recuperação? Pense de novo. Uma nova geração de golpes visa diretamente as interações com **smart contracts**.

## Entendendo as Aprovações de Tokens (Approve)

**O que é uma aprovação?** Uma permissão que você dá a um smart contract para gastar seus tokens em seu nome.

### O Perigo das Aprovações Ilimitadas

Muitas dApps pedem **aprovações ilimitadas** — você autoriza o smart contract a gastar uma quantidade infinita dos seus tokens, para sempre.

## O Golpe do "Permit" e Assinaturas Off-Chain

1. Um site malicioso pede para assinar uma mensagem (**sem gas fees**).
2. A mensagem contém uma autorização \`Permit\`.
3. O hacker usa essa assinatura para esvaziar seus tokens.

> **A armadilha**: Sem gas fees = parece inofensivo. É falso.

## Como a Lumina Wallet Revoluciona a Segurança

| Carteira clássica | Lumina Wallet |
| --- | --- |
| Dados hexadecimais incompreensíveis | **ALERTA: Você está prestes a dar permissão para gastar TODOS seus USDC.** |
| "Assinar mensagem" | **ATENÇÃO: Esta mensagem autoriza a transferência dos seus NFTs.** |

## Boas Práticas

1. **Use uma carteira com simulação de transação**, como **Lumina Wallet**.
2. **Seja extremamente desconfiado** de ofertas boas demais.
3. **Use uma burner wallet** para dApps desconhecidas.
4. **Revogue regularmente** com [Revoke.cash](https://revoke.cash/).

**Não assine mais às cegas.** [Baixe a Lumina Wallet](#) e descubra a verdadeira segurança de transações.`
  },
  "arnaque-smart-contract-trust-wallet-vs-lumina": {
    title: "Golpe de Smart Contract: Por Que Sua Carteira é uma Peneira (e Como a Lumina Te Protege)",
    meta: "Descubra através de uma comparação chocante como um golpe de aprovação ilimitada de smart contract pode esvaziar sua carteira. Veja por que a Lumina Wallet oferece proteção que outros não têm.",
    body: `> **Contexto:** Em 13 de março de 2026, [Le Parisien revelou](https://www.leparisien.fr/faits-divers/arnaque-aux-cryptos-a-paris-les-escrocs-presumes-auraient-depouille-leurs-victimes-de-11-millions-deuros-13-03-2026-D53QN2HAUVFHZGX6YJBD6LKLLA.php) que uma rede de golpistas roubou **11 milhões de euros em criptomoedas**. Método: sites fraudulentos e aprovações de smart contracts manipuladas.

---

Milhões de euros são roubados todo mês por **aprovações ilimitadas de smart contracts**. O maior cúmplice? **Sua própria carteira.**

## O Teste: Um Site de Golpe Real

Testamos o site real de golpe: **bitcoin-suisse-verif.com**.

## 1. Reação da Trust Wallet: O Perigo Invisível

Trust Wallet mostra uma simples confirmação de envio de 0 ETH. Nenhum alerta.

## 2. Reação da Lumina Wallet: O Escudo Ativo

![Alerta de segurança Lumina Wallet - PERIGO detectado](https://customer-assets.emergentagent.com/job_92eb487e-5db1-496a-be31-c7d8444bed8d/artifacts/9ltapzuw_screenshot_danger.png)

- **Alertas Claros**: "PERIGO - Acesso ilimitado" em vermelho vivo.
- **Explicação em Linguagem Humana**.
- **Dissuasão Ativa**: Botão principal é "Rejeitar".

## Comparação Direta

| | Trust Wallet | Lumina Wallet |
|---|---|---|
| **Alerta visual** | Nenhum | PERIGO em vermelho vivo |
| **Explicação do risco** | Nenhuma | Clara e direta |
| **Tipo de aprovação** | Não mencionado | "Acesso ILIMITADO a TODOS os tokens" |
| **Contrato verificado?** | Não mencionado | Alerta "Contrato NÃO VERIFICADO" |
| **Ação padrão** | Aprovar | Rejeitar |

> **"Mesmo golpe, duas carteiras, duas reações. Uma te abandona, a outra te protege."**

**Sua segurança está a um clique.** [Baixe a Lumina Wallet hoje](#) e descubra a tranquilidade de uma carteira que realmente trabalha por você.`
  }
,
  "empoisonnement-adresse-arnaque-crypto": {
    title: "L'Empoisonnement d'Adresse (Address Poisoning): O Novo Golpe Cripto que Custa Milhões",
    meta: "Descubra como o envenenamento de endereço (address poisoning) funciona, por que um simples copiar e colar pode lhe custar milhões, e como a Lumina Wallet o protege.",
    body: `## A ameaça invisível do copiar-colar

No mundo frenético das criptomoedas, onde cada transação conta e a vigilância é essencial, uma nova ameaça insidiosa se espalha: o **envenenamento de endereço**. Um simples copiar-colar, um gesto inofensivo do dia a dia, pode agora custar-lhe milhões. Essa tática sofisticada dos cibercriminosos visa até mesmo os usuários mais experientes, transformando a conveniência em uma armadilha devastadora. À medida que os golpes de criptomoedas continuam a se multiplicar e a se profissionalizar, é crucial entender como esse ataque funciona para melhor se prevenir.

## O que é o Envenenamento de Endereço?

O envenenamento de endereço é uma forma de golpe em que os atacantes **manipulam o histórico das suas transações** para enganá-lo. O objetivo é fazer com que você envie fundos para um endereço malicioso que se assemelha estranhamente a um endereço legítimo com o qual você já interagiu. Os cibercriminosos exploram uma fraqueza humana fundamental: a tendência de verificar apenas os primeiros e últimos caracteres de um endereço longo e complexo.

**Exemplo prático:** Imagine que você envia regularmente fundos para o endereço \\\`0x123...abc\\\`. Um atacante irá gerar um endereço \\\`0x123...abC\\\` (com uma ligeira variação, muitas vezes um único caractere) e injetá-lo no seu histórico de transações. Quando você quiser enviar fundos para o seu contato habitual, poderá, inadvertidamente, copiar o endereço do atacante em vez do correto.

## Como funciona: O mecanismo do golpe

O processo é enganosamente simples:

### 1. Injeção
Os atacantes monitoram a blockchain para identificar carteiras ativas. Eles então enviam pequenas quantidades de criptomoedas (muitas vezes 0 ETH ou uma fração insignificante de um token) de um endereço que eles controlam para a sua carteira. Este endereço é especialmente projetado para se parecer com um endereço com o qual você já realizou transações.

### 2. Histórico
Esta transação mínima, mas maliciosa, aparece no histórico da sua carteira. Ela frequentemente se posiciona perto das suas transações legítimas, aumentando a probabilidade de você notá-la.

### 3. Copiar-colar
Quando você deseja realizar uma nova transação para o seu contato habitual, você pode, por hábito ou desatenção, copiar o endereço do atacante do seu histórico em vez do endereço correto.

### 4. Perda
Uma vez que os fundos são enviados para o endereço do atacante, eles são **irreversivelmente perdidos**. A natureza descentralizada da blockchain torna impossível o cancelamento da transação ou a recuperação dos fundos.

## As Consequências: Uma perda irreversível

A periculosidade do envenenamento de endereço reside no seu caráter sutil e na finalidade das transações blockchain. Uma vez que você validou o envio para o endereço do atacante, não há nenhum recurso. Os fundos são transferidos e desaparecem nos meandros das carteiras maliciosas, frequentemente lavados rapidamente, tornando qualquer recuperação quase impossível.

## Como a Lumina Wallet te protege: O Scanner de Auditoria de Segurança de Endereços

Diante dessa ameaça crescente, a **Lumina Wallet** se posiciona como sua primeira linha de defesa. Nosso Scanner de Auditoria de Segurança de Endereços é projetado para detectar essas armadilhas invisíveis antes que elas lhe custem caro. Veja como funciona:

- **Verificação em tempo real**: Cada endereço com o qual você interage é submetido a uma verificação rigorosa e instantânea.
- **Detecção de endereços maliciosos**: A Lumina não se limita a verificar a validade técnica (EIP-55). Ela cruza as informações com bancos de dados de inteligência de ameaças em tempo real (via parceiros como GoPlus Security) para identificar endereços conhecidos por estarem associados a listas negras, phishing ou honeypots.
- **Alertas claros**: Antes que você aprove uma transação, a Lumina fornece um indicador de segurança claro: **verde** (seguro), **amarelo** (cuidado) ou **vermelho** (perigo/bloqueado). Isso permite que você tome uma decisão informada e evite erros custosos.

Graças à Lumina Wallet, você se beneficia de uma proteção proativa que vai além da simples gestão de chaves privadas, protegendo-o de golpes sofisticados que exploram as falhas humanas.

## Dicas de Prevenção para Usuários de Cripto

Embora a Lumina Wallet ofereça uma proteção avançada, algumas boas práticas permanecem essenciais:

- **Verifique sempre o endereço completo**: Reserve um tempo para verificar cada caractere do endereço de destino, não apenas o início e o fim.
- **Faça um pequeno teste**: Antes de enviar grandes quantias, realize uma pequena transação de teste para garantir que o endereço esteja correto.
- **Use carteiras seguras**: Priorize as carteiras que integram recursos de segurança avançados, como o scanner de endereços da Lumina Wallet.

## Conclusão: A vigilância aumentada pela tecnologia

O envenenamento de endereço é um lembrete brutal de que a segurança no espaço cripto é uma batalha constante. Mas com as ferramentas certas, você não está sozinho. A **Lumina Wallet** é projetada para ser o seu escudo, combinando tecnologia de ponta com uma interface intuitiva para lhe oferecer tranquilidade. Navegue no mundo das criptomoedas com confiança, sabendo que a Lumina Wallet é a sua primeira linha de defesa contra os golpes modernos.

[Baixar Lumina Wallet no Google Play](https://play.google.com/store/apps/details?id=com.luminawallet.app) | [Baixar Lumina Wallet na App Store](https://apps.apple.com/us/app/lumina-wallet/id6760651380)`
  }
,
  "permit2-arnaques-signature-crypto": {
    title: "Permit2 e os Golpes de Assinatura: Proteja Sua Carteira das Armadilhas Invisíveis",
    meta: "Compreenda os perigos das assinaturas Permit2 e das aprovações ilimitadas. Descubra como a Lumina Wallet decodifica cada assinatura para protegê-lo de *wallet drainers*.",
    body: `## Os perigos ocultos das assinaturas de transações

No ecossistema Web3, a assinatura de uma transação é o ato supremo de controle. É o momento em que você dá seu consentimento para uma ação na blockchain. No entanto, com a evolução dos protocolos e o surgimento de novas funcionalidades como **Permit2**, os cibercriminosos encontraram maneiras de transformar esse ato de confiança em uma armadilha invisível. Você pode pensar que está assinando uma simples transação, quando, na realidade, está dando acesso ilimitado aos seus fundos. Compreender os golpes de assinatura tornou-se essencial para qualquer usuário de criptomoedas.

## O que é Permit2 e por que é útil?

Permit2 é um contrato inteligente inovador, inicialmente projetado pela **Uniswap**, para simplificar e otimizar a gestão de aprovações de tokens. Tradicionalmente, para cada novo token que você desejava trocar em uma DEX, era preciso realizar uma transação de aprovação separada, o que custava gas e levava tempo. Permit2 permite agrupar essas aprovações e gerenciá-las via assinaturas "off-chain" (fora da cadeia), tornando as interações com a DeFi muito mais fluidas e econômicas.

## Como funcionam os golpes de Permit2?

Infelizmente, essa eficiência aumentada abriu a porta para novas formas de phishing:

### 1. Websites ou dApps fraudulentos
Os atacantes criam plataformas que imitam serviços DeFi populares. Quando um usuário tenta interagir com esses sites, é solicitada uma assinatura Permit2.

### 2. Assinaturas enganosas
Ao contrário de uma transação clássica, o conteúdo de uma assinatura Permit2 é frequentemente apresentado na forma de **dados hexadecimais brutos**, incompreensíveis para o usuário médio. O atacante aproveita para inserir uma solicitação de aprovação ilimitada para seus próprios endereços.

### 3. Consequências devastadoras
Uma vez que a assinatura é feita, o atacante obtém o direito de transferir seus tokens **sem que você precise assinar uma nova transação**. Sua carteira pode ser esvaziada em um instante, sem nenhum aviso prévio on-chain.

## Por que é tão perigoso: A ameaça invisível das aprovações

O maior perigo dos golpes de assinatura reside em sua **invisibilidade**. Uma assinatura Permit2 não dispara uma transação imediata na blockchain. Ela concede uma autorização que pode ser usada posteriormente pelo atacante. Além disso, a complexidade técnica dos dados assinados torna quase impossível para um usuário desavisado compreender as implicações reais de seu ato. É uma exploração direta da confiança e da falta de transparência.

## O papel da Lumina Wallet: O Decodificador de Transações & Assinaturas

É aqui que a **Lumina Wallet** intervém para transformar a escuridão em clareza. Nosso Decodificador de Transações & Assinaturas é o seu escudo contra as armadilhas invisíveis:

- **Tradução em linguagem clara**: A Lumina decodifica instantaneamente os dados hexadecimais complexos das assinaturas Permit2 e das aprovações ERC-20. Ela mostra precisamente o que você está prestes a assinar, em termos simples e compreensíveis.
- **Transparência total**: Antes de cada assinatura, a Lumina exibe a ação solicitada: trata-se de uma aprovação limitada ou ilimitada? A quem você está dando acesso aos seus fundos?
- **Avaliação de riscos**: Graças ao nosso sistema de risco codificado por cores (Seguro, Baixo, Médio, Alto, Crítico), você é imediatamente alertado se uma assinatura apresenta um perigo potencial.

Com a Lumina Wallet, você nunca mais assina às cegas. Você retoma o controle total de suas interações Web3 graças a uma transparência sem compromissos.

## Boas Práticas para a Segurança das Assinaturas

Para navegar com segurança, adote estes hábitos:

- **Leia atentamente**: Nunca assine uma transação ou uma assinatura sem ter lido e compreendido os detalhes.
- **Use ferramentas de decodificação**: Privilegie carteiras como a Lumina Wallet que traduzem os dados técnicos para uma linguagem clara.
- **Revogue as aprovações**: Adquira o hábito de revogar regularmente as autorizações de tokens que você não utiliza mais.

## Conclusão: Assine com total conhecimento de causa com a Lumina

A segurança na Web3 não deve ser um luxo, mas um padrão. A **Lumina Wallet** se compromete a fornecer as ferramentas necessárias para protegê-lo das ameaças mais sofisticadas. Ao trazer clareza onde havia escuridão, a Lumina permite que você aproveite plenamente a DeFi e as criptomoedas, com total segurança. Assine com confiança, assine com a Lumina.

[Baixar Lumina Wallet no Google Play](https://play.google.com/store/apps/details?id=com.luminawallet.app) | [Baixar Lumina Wallet na App Store](https://apps.apple.com/us/app/lumina-wallet/id6760651380)`
  },
  "faux-wallets-crypto-clones": {
    title: "Wallets Cripto Falsas: Como Reconhecer os Clones que Esvaziam Suas Contas",
    meta: "Os golpistas distribuem aplicações wallet perfeitamente clonadas para roubar suas 12 ou 24 palavras. Descubra os sinais de alerta e como proteger sua carteira Web3.",
    body: `## A anatomia de uma wallet falsa

Os golpistas investem recursos consideráveis para criar réplicas perfeitas das wallets mais populares. Esses clones copiam logotipos, cores, fontes e a interface exata dos aplicativos legítimos. A olho nu é quase impossível distinguir uma falsificação do original.

A armadilha se fecha em três etapas:

### 1. Distribuição fraudulenta
Wallets falsas são distribuídas por meio de anúncios enganosos em motores de busca, mensagens privadas em redes sociais (X, Telegram, Discord) ou contornando temporariamente os filtros das lojas oficiais.

### 2. A ilusão da atualização ou do erro
Após a instalação, o clone simula um problema técnico: um falso erro de conexão, um saldo bloqueado ou uma suposta "atualização de segurança obrigatória".

### 3. Extração da frase secreta
Para "resolver" o falso problema, o app pede que você digite suas 12 ou 24 palavras. Assim que você confirma, elas são enviadas em texto simples ao servidor do atacante, que esvazia instantaneamente todas as suas contas.

## Sinais de alerta que você nunca deve ignorar

- **A solicitação da frase de recuperação** — Uma wallet legítima, um suporte técnico ou um site **nunca** pedirá que você digite sua frase de recuperação para resolver um problema. Suas 12 ou 24 palavras servem apenas para restaurar contas em um novo dispositivo.
- **Pop-ups inexplicáveis** — Se um pop-up pedindo login aparecer de repente enquanto você navega em um site não relacionado a cripto, feche-o imediatamente.
- **Origem do download** — Baixe wallets e extensões apenas do site oficial. Nunca clique em links recebidos por email ou mensagem privada. Verifique a URL com atenção ("rn" em vez de "m").

## Segurança do dispositivo vs. segurança on-chain

Uma wallet não-custodial como **Lumina Wallet** analisa as transações, decodifica smart contracts e alerta você antes de enviar fundos para um endereço perigoso. Cruza os dados com bases de ameaças em tempo real para bloquear phishing on-chain.

No entanto, **nenhuma wallet pode protegê-lo se o dispositivo estiver comprometido por malware** ou se você inserir voluntariamente sua frase de recuperação em um app falso.

## Sua checklist de segurança

- Baixe Lumina Wallet apenas em [luminawallet.org](https://luminawallet.org) ou nas lojas oficiais.
- Verifique o editor: **HÉDONISME SARL** para Lumina Wallet.
- Mantenha o sistema operacional e os apps atualizados.
- Nunca insira sua frase de recuperação, exceto para restaurar voluntariamente uma conta em um dispositivo confiável.

## Conclusão

A segurança Web3 exige uma higiene digital global. Lumina protege suas interações on-chain; cabe a você proteger o ambiente em que opera.

[Baixar no Google Play](https://play.google.com/store/apps/details?id=com.luminawallet.app) | [Baixar na App Store](https://apps.apple.com/us/app/lumina-wallet/id6760651380)`
  },
  "faux-support-arnaques-recuperation-crypto": {
    title: "Falso Suporte e Golpes de Recuperação: O Duplo Golpe às Vítimas Cripto",
    meta: "Após um hack, uma segunda onda de golpistas promete recuperar seus fundos em troca de um pagamento. Aprenda a reconhecer os falsos serviços de recuperação e falsos agentes de suporte.",
    body: `> Procura os passos concretos após um roubo? Leia [Criptos roubadas: o que fazer nas primeiras 24 horas](/pt/blog/crypto-volees-que-faire).

## O duplo golpe

Perder criptomoedas por descuido, hack ou golpe é uma experiência traumática. No pânico que se segue, a vítima procura desesperadamente uma solução. É exatamente nesse momento de máxima vulnerabilidade que ataca uma segunda categoria de fraudadores: **os falsos serviços de recuperação e os falsos agentes de suporte técnico**.

Esses golpes, muitas vezes chamados de "fraude de adiantamento de taxas", baseiam-se em uma cruel manipulação psicológica.

> **Definição — golpe de recuperação:** segunda fraude que ataca uma pessoa já lesada, prometendo recuperar seus fundos em troca de pagamento, dados sensíveis ou acesso ao dispositivo.

## O golpe de recuperação

Os golpistas mantêm listas de vítimas e monitoram ativamente fóruns, redes sociais e comentários. A mecânica:

1. **O contato providencial** — Um suposto especialista em cibersegurança, "hacker ético" ou falso agente de uma autoridade entra em contato afirmando ter rastreado seus fundos.
2. **A falsa prova** — Ele mostra um screenshot de uma wallet cheia ou um site com saldo igual às suas perdas. Ambos estão sob seu controle.
3. **A armadilha das taxas** — Seus fundos estão "prontos para serem transferidos", mas você precisa pagar antes: "imposto de desbloqueio", "taxas de rede" ou "comissão jurídica".
4. **A engrenagem** — Se você paga, eles inventarão novos pretextos para exigir mais pagamentos. Nenhum fundo será jamais devolvido.

## O falso suporte técnico

Uma variante muito comum é a de se passar pelo suporte técnico de uma wallet ou exchange conhecida. Esses falsos agentes operam em X, Telegram ou Discord. Assim que você faz uma pergunta pública, eles o contatam em privado com o logo oficial e tom profissional.

O objetivo é sempre o mesmo: convencê-lo a compartilhar sua frase de recuperação ou clicar em um link de phishing.

## Os sinais que denunciam o golpe

- **As transações cripto são irreversíveis.** Nenhuma empresa, hacker ou autoridade pode "cancelar" uma transação confirmada.
- **O suporte nunca entra em contato primeiro.** Equipes legítimas nunca fazem DM espontânea, SMS ou telefone.
- **A frase secreta é tabu.** Nenhum suporte técnico precisa dela.
- **As autoridades não pedem cripto.** Polícia ou reguladores nunca o contatarão espontaneamente para devolver fundos.

## Tabela de promessas enganosas

| Promessa recebida | Verdadeiro objetivo do golpista | Resposta segura |
|---|---|---|
| "Encontramos sua wallet." | Criar esperança urgente antes de pedir taxas. | Não pague; verifique por canais oficiais. |
| "Pague uma taxa de liberação." | Um primeiro pagamento seguido de outros. | Interrompa o contato; guarde a mensagem como prova. |
| "Instale este software para eu ajudar." | Tomar controle do dispositivo ou roubar seus segredos. | Recuse qualquer acesso remoto. |
| "Dê-me suas 12/24 palavras para ressincronizar." | Controle definitivo das suas contas. | Nunca compartilhe sua frase de recuperação. |

## Como reagir

1. **Corte toda comunicação** com os golpistas.
2. **Proteja o que resta** — migre os ativos para uma nova wallet em um dispositivo limpo; revogue autorizações suspeitas.
3. **Preserve as provas** — URLs, capturas, endereços cripto, hashes de transação.
4. **Denuncie e registre queixa** junto às autoridades competentes.

## A transparência antes de tudo

Na Lumina Wallet construímos ferramentas para impedir que você envie fundos a golpistas ou aprove contratos perigosos. Nossos scanners analisam endereços e transações antes de você assinar.

No entanto, somos transparentes sobre os limites da tecnologia: **se uma transação fraudulenta for confirmada na blockchain, a Lumina não pode desfazê-la**. Desconfie de quem afirmar o contrário.

[Baixar no Google Play](https://play.google.com/store/apps/details?id=com.luminawallet.app) | [Baixar na App Store](https://apps.apple.com/us/app/lumina-wallet/id6760651380)`
  },
  "qr-code-copier-coller-detournement-crypto": {
    title: "Códigos QR e Copiar-Colar: Quando o Endereço de Destino Mente Para Você",
    meta: "Malware da área de transferência, QR codes de pagamento falsos, adesivos fraudulentos: descubra como os golpistas desviam seus envios cripto e como a Lumina Wallet protege você.",
    body: `## O gesto cotidiano que sai caro

No universo cripto, os endereços são longas sequências alfanuméricas complexas. Ninguém os digita à mão. Para transferir fundos, todos dependemos de dois gestos cotidianos: **copiar-colar** e **escaneamento de QR code**.

Os golpistas sabem disso. Em vez de tentar quebrar a criptografia da blockchain, eles miram esses dois gestos para desviar seus fundos no exato momento em que você inicia um pagamento.

> **Definição — desvio de destino:** manipulação que substitui ou mascara o endereço realmente usado em um pagamento cripto, enquanto o usuário acredita ainda estar enviando para o destinatário legítimo.

## O sequestro da área de transferência (Clipboard Hijacking)

O ataque por sequestro do clipboard é temível pela sua simplicidade e discrição. Depende de um malware instalado sem o seu conhecimento.

1. Você copia o endereço cripto legítimo do destinatário.
2. O malware, que vigia permanentemente sua área de transferência, detecta o formato de endereço cripto.
3. Em uma fração de segundo, substitui o endereço legítimo pelo do pirata.
4. Ao colar no wallet, aparece o endereço do pirata.

Se você validar sem reler, os fundos vão embora.

## As armadilhas dos QR codes

QR codes simplificam pagamentos móveis, mas escondem a informação real. Um QR é apenas uma representação visual: pode conter um endereço cripto, um pedido de pagamento, uma URI WalletConnect ou um link de phishing.

Métodos comuns:

- **Portal de pagamento falso** — O QR redireciona a um site que imita perfeitamente uma plataforma legítima.
- **Imposição por telefone (alerta FTC)** — Um golpista se passando por autoridade ou atendimento mantém você ao telefone, leva a um caixa cripto e pede para escanear um QR que contém o endereço do próprio wallet dele.
- **Adesivo fraudulento** — No mundo físico, criminosos colam seu QR sobre o QR legítimo de um comerciante.

## Tabela resumo dos vetores

| Vetor | O que o usuário vê | O que realmente acontece | Verificação prioritária |
|---|---|---|---|
| Clipboard sequestrado | Endereço recém-copiado de fonte confiável. | Malware substituiu antes da colagem. | Comparar endereço colado com a fonte original. |
| QR de pagamento fraudulento | Código apresentado como do beneficiário. | Contém o endereço do golpista. | Confirmar por um segundo canal. |
| QR de phishing | Acesso rápido a um serviço. | Abre um domínio enganoso. | Ler o domínio e a ação antes de continuar. |
| Adesivo sobreposto | QR em um local físico. | O código original foi coberto. | Examinar a superfície; pedir confirmação. |

## Reflexos para proteger seus envios

- **Verifique sempre após colar**: compare os primeiros e últimos caracteres com a fonte original.
- **Desconfie de QR inesperados** recebidos por email, SMS ou redes sociais.
- **Proteja seus dispositivos** com SO atualizado e antivírus reputado.
- **Envio de teste** para novos endereços e valores altos (embora dobre as taxas).
- **Releia a tela de confirmação**: rede, ativo, valor e endereço final.

## A abordagem Lumina: auditoria antes do envio

O erro humano é inevitável, por isso um wallet moderno deve agir como rede de segurança. Em vez de um scanner universal que pode redirecioná-lo a um site malicioso, a Lumina separa os usos. O scanner na tela de envio lê apenas endereços cripto. Se detectar um link web ou comando suspeito, bloqueia a ação imediatamente.

Seja você colando ou escaneando, a Lumina não se limita a aceitar o endereço: realiza uma **auditoria de segurança instantânea**:

- Verificação do formato.
- Cruzamento em tempo real com bases de ameaças (como GoPlus Security).
- Alerta claro antes da confirmação.

Você mantém o controle total, mas obtém as informações necessárias para detectar um desvio antes que seja tarde demais.

[Baixar no Google Play](https://play.google.com/store/apps/details?id=com.luminawallet.app) | [Baixar na App Store](https://apps.apple.com/us/app/lumina-wallet/id6760651380)`
  },
  "how-to-secure-crypto-wallet": {
    title: "Como proteger a sua carteira cripto em 2026: o guia anti-golpes completo",
    meta: "Aprenda a proteger a sua carteira cripto com 10 regras essenciais usadas por especialistas em segurança. Proteja a sua seed phrase, identifique carteiras falsas, bloqueie assinaturas maliciosas e mantenha o seu Bitcoin, Ethereum, Solana e XRP seguros em 2026.",
    body: `Se você possui criptomoedas, uma pergunta importa mais do que qualquer gráfico de preços: **como proteger a sua carteira cripto** para que um atacante nunca consiga chegar a ela. Em 2026, mais de US$ 3,8 bilhões foram roubados de usuários de cripto — e a grande maioria veio de **ataques ao nível da carteira**, não de hacks a exchanges [1]. Isso significa que a pessoa mais responsável pela sua segurança é você.

A boa notícia: proteger uma carteira cripto não é complicado. É uma série de hábitos simples e deliberados. Este guia apresenta o método exato de 10 passos usado por usuários atentos à segurança para proteger Bitcoin, Ethereum, Solana, XRP e qualquer outro ativo digital — e mostra como um app criado para esse fim, como a **Lumina Wallet**, faz o trabalho pesado por você.

## Por que "como proteger a minha carteira cripto" é a pergunta errada

A maioria dos guias responde à pergunta *"qual é a melhor carteira?"*. Mas a pergunta real é: **como proteger a carteira que eu já tenho?** Porque, em cripto, a sua carteira é tão forte quanto o seu pior hábito. Uma hardware wallet não pode salvá-lo se você digitar a sua seed phrase num site de phishing. Uma carteira non-custodial não pode protegê-lo se você assinar um contrato malicioso.

Por isso, este guia se concentra em **hábitos + ferramentas**, na exata ordem de importância.

## As 10 regras para proteger a sua carteira cripto

### 1. Escolha uma carteira non-custodial — sem exceções

A primeira regra de **como proteger uma carteira cripto**: nunca deixe outra pessoa guardar as suas chaves.

- ❌ **Carteiras custodiais** (a maioria das exchanges, alguns apps): a plataforma detém as suas chaves privadas. Se forem hackeadas, congelarem contas ou falirem — as suas criptomoedas desaparecem.
- ✅ **Carteiras non-custodial** (como Lumina Wallet, MetaMask, Trust Wallet): você detém as chaves. Nada entre você e os seus fundos.

O mantra a lembrar: **"Not your keys, not your coins."** (Se as chaves não são suas, as moedas também não são.)

### 2. Proteja a sua seed phrase como se fossem as economias de toda a sua vida (porque são)

A sua seed phrase de 12 ou 24 palavras é a chave mestra. Qualquer pessoa que a tenha pode esvaziar a sua carteira em 30 segundos — de qualquer lugar do mundo.

**Como guardá-la corretamente:**

- 📝 **Escreva-a em papel** (ou grave em metal). Nunca de forma digital.
- 🚫 **Nunca tire uma foto** dela. Nunca a guarde no iCloud, Google Drive, Notas ou em qualquer gerenciador de senhas conectado à internet.
- 🔒 **Faça 2-3 cópias físicas** e guarde-as em locais seguros diferentes (um cofre em casa, um familiar de confiança, um cofre de banco).
- 🗣️ **Nunca a partilhe com ninguém**. Nem com o suporte ao cliente. Nem com "desenvolvedores da carteira". Nem com "agentes da Receita". Ninguém legítimo jamais a pedirá.

> ⚠️ **Regra de ouro**: se uma pessoa, site ou app pedir a sua seed phrase, é 100% golpe. Zero exceções.

### 3. Use uma senha de carteira forte e única

A senha da sua carteira criptografa localmente as suas chaves no seu dispositivo. Use:

- Pelo menos **14 caracteres**
- Uma mistura de maiúsculas, minúsculas, números e símbolos
- **Diferente de qualquer outra senha que você use**
- Guardada num gerenciador de senhas (Bitwarden, 1Password, KeePass)

### 4. Ative a autenticação biométrica + PIN

As carteiras cripto modernas suportam Face ID, leitura de impressão digital e PIN de 6 dígitos. Ative **as três camadas**. Mesmo que alguém roube o seu telefone, não conseguirá abrir a sua carteira sem a sua biometria.

### 5. Cuidado com apps de carteira falsos

Golpistas publicam **apps clonados** no Google Play e na App Store com nomes como *"Lumina Wallet Pro"*, *"Lumina — Free Airdrop"* ou com logótipos imitados. Parecem idênticos, mas roubam a sua seed phrase no momento em que você a insere.

**Como evitar carteiras falsas:**
- ✅ Baixe apenas a partir dos **links oficiais** no site do projeto (por exemplo, https://luminawallet.org)
- ✅ Verifique se o **nome do desenvolvedor** corresponde (no caso da Lumina Wallet: *Lumina Wallet* por HEDONISME SARL)
- ✅ Confira o número de downloads e avaliações — os clones geralmente têm <100 downloads
- ✅ Leia o nosso guia completo sobre [clones de carteiras falsas](/en/blog/fake-crypto-wallet-clones)

### 6. Verifique cada endereço de destino antes de enviar

Os ataques de address poisoning estão explodindo: golpistas enviam-lhe uma transação minúscula a partir de um endereço que **se parece com** um dos seus habituais (mesmos 4 primeiros e 4 últimos caracteres, meio diferente). Você copia e cola do seu histórico e envia os seus fundos diretamente para o atacante.

**Como evitar:**
- ✅ Verifique sempre **o endereço completo**, não apenas o início e o fim
- ✅ Use uma carteira com **agenda de endereços** para guardar contatos de confiança (a Lumina tem uma integrada)
- ✅ Antes de transferências grandes, envie primeiro uma **pequena transação de teste**

### 7. Nunca assine uma transação que você não entende

O golpe mais técnico de 2026 é a **assinatura maliciosa**: você clica em "Assinar" no que parece um botão de login, mas na verdade está aprovando um limite de gasto infinito sobre os seus USDC/ETH. Os atacantes esvaziam a carteira no dia seguinte.

**Checklist de proteção antes de assinar:**
- 🔍 Leia os dados da transação — que token? Que quantidade? Que contrato?
- 🚨 **"Unlimited approval"** ou **"setApprovalForAll"** = sinal de alerta
- 🚨 **Pedido de assinatura de um site em que acabou de chegar** = sinal de alerta
- 🚨 A carteira diz *"Esta transação pode ser perigosa"* = PARE

Uma carteira com **scanner de transações** (como a Lumina Wallet) detecta esses ataques antes de você assinar.

### 8. Mantenha o app da carteira e o sistema do telefone atualizados

Cada atualização da carteira inclui **correções de segurança** para vulnerabilidades recém-descobertas. O mesmo vale para o sistema operacional do seu telefone. Um app desatualizado é uma superfície de ataque conhecida.

- 📱 Ative as **atualizações automáticas** no Google Play e na App Store
- 🔐 Atualize o sistema do telefone no prazo de uma semana após qualquer lançamento de segurança

### 9. Nunca conecte a sua carteira a sites desconhecidos

As DApps são poderosas — mas conectar a sua carteira a um site fraudulento pode esvaziá-la com uma única assinatura.

**Regras:**
- Conecte-se apenas a **URLs que você mesmo digitou** ou que vieram de canais oficiais nas redes sociais
- Nunca se conecte através de um link numa DM do Discord/Telegram
- Depois de usar uma DApp, **revogue as permissões** em [revoke.cash](https://revoke.cash)
- Guarde as DApps de confiança nos favoritos para nunca precisar procurá-las no Google (os anúncios do Google estão cheios de clones fraudulentos)

### 10. Use uma carteira com proteção anti-golpes integrada

Você é humano. Vai ficar cansado. Vai clicar rápido demais. É por isso que a última linha de defesa é uma carteira que **detecta golpes por você** — antes que cometa um erro custoso.

## Como a Lumina Wallet protege as suas criptomoedas automaticamente

A Lumina Wallet foi criada especificamente para eliminar o maior ponto único de falha em cripto: o **erro humano sob pressão**. Veja como ela o protege em cada etapa:

| Ameaça | Como a Lumina bloqueia |
|---|---|
| **Endereço de phishing** | O scanner em tempo real verifica cada destinatário numa lista negra atualizada com mais de 500.000 endereços fraudulentos conhecidos. Alerta vermelho se houver correspondência. |
| **Smart contract malicioso** | Antes de você assinar, a Lumina simula a transação e mostra *exatamente* quais tokens saem da sua carteira. Se detectar aprovações ilimitadas ou padrões de drenagem, você recebe um aviso crítico. |
| **Address poisoning** | A Lumina sinaliza automaticamente endereços parecidos com o seu histórico — mesmo com 1 caractere de diferença — antes de você enviar. |
| **Golpes de fake permit / assinatura** | As assinaturas ERC-20 Permit e Permit2 são decodificadas e explicadas em linguagem simples. Chega de assinar às cegas. |
| **Non-custodial por design** | As suas chaves são criptografadas no seu dispositivo com AES-256 + Secure Enclave. Os servidores da Lumina nunca as veem. Nem as autoridades, hackers ou uma falência. |
| **Biometria + PIN + senha** | Bloqueio em 3 camadas, todas opcionais para configurar ao seu nível de conforto. |

## E as hardware wallets?

As hardware wallets (Ledger, Trezor) adicionam uma camada extra forte para **posições de longo prazo** (>US$ 10 mil que você não mexe semanalmente). Mas elas não substituem as 10 regras acima — somam-se a elas. Mesmo com uma Ledger, uma assinatura maliciosa que você aprove esvaziará os seus fundos.

Para uso diário (swaps, transferências, DeFi), uma **carteira móvel segura com scanner de transações** costuma ser *mais segura* do que uma hardware wallet isolada, porque detecta os ataques de erro humano que as hardware wallets não conseguem impedir.

## Erros comuns que arruínam a segurança da carteira

Mesmo usuários que conhecem as regras cometem estes erros. Evite-os:

- ❌ **Tirar screenshot da seed phrase** "só para backup" — o iCloud/Google Fotos sincroniza-a na nuvem
- ❌ **Usar a mesma senha** na carteira e no e-mail
- ❌ **Clicar em anúncios do Google** de apps de carteira ou DApps (anúncios fraudulentos são comuns)
- ❌ **Guardar cripto em exchanges** a longo prazo "porque é mais fácil"
- ❌ **Ignorar avisos de segurança** por estar com pressa
- ❌ **Testar "airdrops" de desconhecidos** conectando a sua carteira principal

## Checklist rápido de segurança (guarde este)

Antes de investir mais um euro em cripto, confirme que cumpriu os 10 pontos:

- [ ] Uso uma carteira non-custodial (eu detenho as minhas chaves)
- [ ] A minha seed phrase está escrita em papel, em 2+ locais físicos
- [ ] A minha seed phrase nunca tocou na internet
- [ ] A senha da minha carteira tem 14+ caracteres e é única
- [ ] Biometria + PIN estão ativados
- [ ] Baixei a minha carteira apenas do site oficial
- [ ] Verifico sempre os endereços de destino completos
- [ ] Nunca assino transações que não entendo
- [ ] A minha carteira e o sistema do telefone estão atualizados
- [ ] Só me conecto a DApps em que confio explicitamente

## Conclusão: segurança é um hábito, não uma configuração única

Você não pode terceirizar a segurança da carteira. Mas pode torná-la 10x mais fácil combinando **bons hábitos** com **ferramentas que o protegem quando você escorrega**. É exatamente isso que a Lumina Wallet faz — uma carteira multi-chain non-custodial com scanner anti-golpes integrado, verificação de endereços e simulação de transações.

Cada transação é verificada. Cada assinatura é decodificada. Cada risco é explicado em linguagem simples, antes de você se comprometer.

**Pronto para proteger a sua carteira cripto da maneira certa?**

[Baixe a Lumina Wallet no Google Play](https://play.google.com/store/apps/details?id=com.luminawallet.app) | [Baixe na App Store](https://apps.apple.com/us/app/lumina-wallet/id6760651380)

---

### Referências

[1] Chainalysis. (2026). *2026 Crypto Crime Report: Wallet-level attacks*. [https://www.chainalysis.com/blog/crypto-scams-2026/](https://www.chainalysis.com/blog/crypto-scams-2026/)`
  },
  "what-is-non-custodial-wallet": {
    title: "O que é uma carteira non-custodial? O guia completo para iniciantes (2026)",
    meta: "Uma carteira non-custodial permite que você seja dono das suas chaves cripto — sem banco, sem exchange, sem intermediários. Entenda o que significa non-custodial, como ela difere das carteiras custodiais e veja exemplos reais como Lumina Wallet, MetaMask e Trust Wallet.",
    body: `Uma **carteira non-custodial** é uma carteira de criptomoedas em que **você — e somente você — possui as chaves privadas** que controlam os seus fundos. Nenhum banco, nenhuma exchange, nenhum terceiro pode acessar, congelar ou perder as suas criptomoedas. Se você é novo no mundo cripto, este é o conceito mais importante a entender. Este guia explica o que é uma carteira non-custodial, como ela funciona, exemplos do mundo real e se ela é a escolha certa para você.

## Significado de carteira non-custodial: a definição em 30 segundos

**Definição de carteira non-custodial**: uma carteira de criptomoedas em que o usuário mantém a propriedade total das suas chaves privadas. Nenhum terceiro tem a custódia dos fundos. O usuário é o único custodiante.

Em linguagem simples: **se ninguém pode bloquear o seu acesso ao seu dinheiro porque uma empresa decidiu congelar a sua conta, você tem uma carteira non-custodial.**

### Carteira custodial vs non-custodial: comparação rápida

| Característica | Carteira custodial | Carteira non-custodial |
|---|---|---|
| **Quem guarda as suas chaves?** | A exchange/empresa | Você |
| **As contas podem ser congeladas?** | Sim, pela empresa | Não — ninguém pode congelá-la |
| **E se a empresa falir?** | Os fundos podem ser perdidos ou bloqueados | Os fundos estão 100% seguros |
| **É preciso lembrar de uma seed phrase?** | Não (eles a guardam) | Sim (você a guarda) |
| **Risco de apreensão pelo governo?** | Sim (via intimação judicial) | Somente se você for forçado a entregar as chaves |
| **Exemplos** | Coinbase, Binance, Kraken (a carteira padrão deles) | Lumina Wallet, MetaMask, Ledger, Trust Wallet |

## Como funciona uma carteira non-custodial?

Quando você configura uma carteira non-custodial, três coisas acontecem localmente no seu dispositivo:

1. **Uma chave privada é gerada** — um número aleatório enorme que controla matematicamente os seus endereços de cripto.
2. **Uma seed phrase é criada** — 12 ou 24 palavras legíveis por humanos que podem regenerar a chave privada. Este é o seu único backup.
3. **Um endereço público é derivado** — o "endereço de recebimento" que você compartilha com outras pessoas.

A chave privada **nunca sai do seu dispositivo**. Quando você envia cripto, a sua carteira assina a transação localmente e depois transmite apenas a assinatura para a blockchain. Nenhum servidor vê a sua chave. É por isso que se chama *non-custodial*: nenhum custodiante externo, jamais.

## Exemplo de carteira non-custodial: uma transação real

Veja o que acontece quando você envia 0.1 ETH de uma carteira non-custodial como a Lumina:

1. Você insere o endereço do destinatário e o valor
2. A sua carteira **prepara** os dados da transação (origem, destino, valor, taxa de gas)
3. A sua carteira **assina** a transação usando a sua chave privada — isso acontece 100% no seu celular
4. A **transação assinada** (não a chave) é enviada para a rede Ethereum
5. Mineradores/validadores verificam se a assinatura é matematicamente válida
6. A transação é confirmada on-chain

Em nenhum momento qualquer empresa tem o poder de reverter, bloquear ou congelar essa transferência. Essa é a liberdade — e a responsabilidade — das carteiras non-custodial.

## Por que escolher uma carteira cripto non-custodial?

### 1. Você é o banco

O motivo número um: **propriedade verdadeira**. Em 2022, a FTX colapsou e bilhões em fundos de usuários ficaram inacessíveis da noite para o dia. Usuários com carteiras non-custodial não foram afetados de forma alguma. O mesmo aconteceu em 2023 com Celsius, Voyager e BlockFi.

> **"Not your keys, not your coins."** ("Sem as suas chaves, sem as suas moedas.") — Este mantra de poucas palavras já evitou milhões em perdas no mundo cripto.

### 2. Privacidade total

Uma exchange custodial exige KYC (verificação de identidade), rastreia cada transação e reporta às autoridades fiscais e aos reguladores. Uma carteira non-custodial só precisa da sua seed phrase — sem e-mail, sem documento, sem cadastro. Você pode criar uma em 30 segundos.

### 3. Sem limites de saque nem contas congeladas

As plataformas custodiais congelam contas por "atividade suspeita", revisões regulatórias ou janelas de manutenção — às vezes por semanas. As carteiras non-custodial não têm esse tipo de atrito: os seus fundos estão sempre acessíveis, 24 horas por dia, 7 dias por semana, de qualquer lugar com internet.

### 4. Acesso a DeFi e Web3

Quase todo protocolo DeFi (Uniswap, Aave, Lido), marketplace de NFT (OpenSea, Magic Eden) e aplicativo Web3 exige uma carteira non-custodial para se conectar. Carteiras custodiais não podem participar de aplicativos on-chain.

## O que envolve o desenvolvimento de uma carteira non-custodial?

Para desenvolvedores curiosos sobre o **desenvolvimento de carteiras non-custodial**, esta é a stack técnica:

- 🔐 **Geração de chaves**: BIP-32 (chaves determinísticas hierárquicas) + BIP-39 (seed phrases mnemônicas) + BIP-44 (derivação multi-conta)
- 🔗 **Suporte multi-chain**: provedores RPC ou nós próprios para cada blockchain (Bitcoin, Ethereum, Solana, XRP, etc.)
- 💾 **Armazenamento local seguro**: iOS Secure Enclave / Android Keystore para criptografar as chaves
- ✍️ **Assinatura de transações**: ECDSA para Bitcoin/Ethereum, Ed25519 para Solana, tudo feito no lado do cliente
- 🛡️ **Camadas de segurança**: verificação anti-golpes, agenda de endereços, simulação de transações

A Lumina Wallet é uma **carteira móvel non-custodial** construída com tudo isso, além de um **scanner anti-golpes integrado** — porque propriedade sem proteção é uma armadilha para iniciantes.

## A única contrapartida: a responsabilidade é sua

Uma carteira non-custodial significa que você é dono das suas chaves — mas também significa que **você é 100% responsável pela segurança delas**.

- 🔴 **Perdeu a seed phrase = perdeu as suas criptomoedas**. Não existe botão "esqueci a senha". Nenhum atendimento ao cliente pode ajudar.
- 🔴 **Caiu em phishing = perdeu as suas criptomoedas**. Ninguém pode reverter uma transação assinada.
- 🔴 **Assinou um contrato malicioso = perdeu as suas criptomoedas**. A responsabilidade é inteiramente sua.

É por isso que carteiras non-custodial modernas, como a Lumina, adicionam camadas de proteção:
- ✅ Scanner de golpes em tempo real que bloqueia endereços de phishing conhecidos
- ✅ Simulação de transações antes de assinar
- ✅ Alertas claros para assinaturas suspeitas de "Permit" ou "Approve"
- ✅ Agenda de endereços para evitar erros de digitação e ataques de copiar e colar

## Exemplos de carteiras non-custodial em 2026

As melhores carteiras cripto non-custodial atualmente, com base em segurança, facilidade de uso e suporte multi-chain:

| Carteira | Tipo | Redes | Ideal para |
|---|---|---|---|
| **Lumina Wallet** | Móvel | 10+ (BTC, ETH, SOL, XRP, BNB, POL...) | Uso diário + proteção contra golpes |
| **MetaMask** | Navegador + Móvel | Redes EVM, Solana e Bitcoin | Usuários avançados de DeFi |
| **Trust Wallet** | Móvel | 70+ | Usuários casuais multi-chain |
| **Ledger** | Hardware | 100+ | Armazenamento a frio de longo prazo |
| **Rabby** | Navegador | Ecossistema Ethereum | DeFi avançado com simulação de transações |

## Carteira móvel non-custodial: é segura?

Sim — se você seguir práticas básicas de segurança. Uma carteira móvel non-custodial costuma ser **mais segura do que uma exchange** pelos seguintes motivos:

- As suas chaves são criptografadas com o módulo de segurança de hardware do seu celular
- Você conta com autenticação biométrica + PIN
- Carteiras modernas (como a Lumina) verificam cada transação em busca de golpes
- Não há um único "alvo" para hackers — 100 milhões de carteiras = 100 milhões de alvos separados

Para valores abaixo de €10,000 que você usa regularmente, uma carteira móvel non-custodial bem projetada oferece o melhor equilíbrio entre segurança e usabilidade.

## Como começar com a sua primeira carteira non-custodial

1. **Baixe o aplicativo** no site oficial (não pela busca na loja de aplicativos — existem clones)
2. **Crie uma nova carteira** — o aplicativo gera as suas chaves localmente
3. **Anote a seed phrase de 12 palavras** em papel, em um local seguro
4. **Defina uma senha forte** e ative a biometria
5. **Envie primeiro um pequeno valor de teste** para verificar se tudo funciona
6. **Leia o nosso [guia de segurança da carteira em 10 passos](/en/blog/how-to-secure-crypto-wallet)** antes de guardar valores elevados

## Perguntas frequentes sobre carteiras non-custodial

### Uma carteira non-custodial é a mesma coisa que uma hot wallet?

Não exatamente. *Hot wallet* significa "conectada à internet". *Non-custodial* significa "o usuário possui as chaves". Uma carteira pode ser hot E non-custodial (Lumina, MetaMask) — ou non-custodial e cold (Ledger, Trezor).

### O que acontece se eu perder o meu celular?

Nada de ruim — desde que você tenha a sua seed phrase. Instale a carteira em um novo celular, importe a seed phrase e os seus fundos voltam instantaneamente.

### Posso converter a minha carteira custodial em non-custodial?

Você não pode "convertê-la" — mas pode sacar os seus fundos de uma exchange custodial para uma nova carteira non-custodial que você criou. Este é o caminho recomendado para quem possui valores significativos em cripto.

### Carteiras non-custodial pagam impostos?

As carteiras non-custodial não reportam automaticamente às autoridades fiscais — mas *você* continua devendo impostos sobre os ganhos. Use ferramentas como Koinly ou CoinTracker para calcular os seus impostos a partir do histórico de transações da sua carteira.

## Conclusão: seja dono das suas criptomoedas de verdade

Se você leva cripto a sério, uma carteira non-custodial não é opcional — é a base. Todos os anos, usuários perdem fortunas com falências de exchanges, congelamentos de contas ou hacks de plataformas. Nada disso pode atingir uma carteira non-custodial devidamente protegida.

**A Lumina Wallet é uma carteira multi-chain non-custodial** com scanner de golpes integrado, agenda de endereços, simulação de transações e suporte a mais de 10 blockchains — criada para quem quer propriedade *e* proteção.

[Baixe a Lumina Wallet no Google Play](https://play.google.com/store/apps/details?id=com.luminawallet.app) | [Baixe na App Store](https://apps.apple.com/us/app/lumina-wallet/id6760651380)`
  },
  "lumina-wallet-vs-lumina-token": {
    title: "Lumina Wallet vs Lumina Coin, Pay, Chain: não confunda os projetos",
    meta: "Em dúvida entre a Lumina Wallet e outros projetos cripto chamados \"Lumina\" (Lumina Coin, Lumina Pay, Lumina Chain, Lumina Crest AI)? Veja as diferenças claras para não cair numa armadilha de nomes parecidos.",
    body: `Pesquisou "Lumina crypto" no Google e encontrou vários projetos diferentes usando o nome "Lumina"? É normal — e é exatamente essa confusão que queremos esclarecer. **A Lumina Wallet NÃO tem nenhuma ligação com os projetos "Lumina Coin", "Lumina Pay", "Lumina Chain Base" ou "Lumina Crest AI"** que você pode encontrar online. Veja como distingui-los claramente.

## Quem é realmente a Lumina Wallet?

A **Lumina Wallet** é um aplicativo de carteira cripto móvel **non-custodial** (sem custódia), publicado pela **HEDONISME SARL** (empresa francesa registrada no RCS Dunkerque sob o número 901 439 505).

### Nossas características únicas:
- 📱 **Aplicativo móvel** disponível no [Google Play](https://play.google.com/store/apps/details?id=com.luminawallet.app) e na [App Store](https://apps.apple.com/us/app/lumina-wallet/id6760651380)
- 🔐 **Non-custodial**: só você detém suas chaves privadas
- 🌐 **Multi-chain**: Bitcoin, Ethereum, Solana, XRP, BNB, Polygon e mais de 5 outras blockchains
- 🛡️ **Scanner antigolpes integrado**: é a nossa principal diferenciação
- 🇫🇷 **Empresa francesa** verificável no registro comercial RCS
- 🌍 **Um único site oficial**: [luminawallet.org](https://luminawallet.org)

### O que a Lumina Wallet NÃO É:
- ❌ **NÃO é um token / criptomoeda** — não temos token nativo
- ❌ **NÃO é uma blockchain layer-1 ou layer-2** — nos conectamos a blockchains existentes
- ❌ **NÃO é uma plataforma de investimento automatizado** — não oferecemos "rendimentos garantidos"
- ❌ **NÃO é um serviço de pagamento B2B** — somos uma carteira para o consumidor final
- ❌ **NÃO é um projeto de IA** — somos um aplicativo de segurança cripto

## Os outros projetos "Lumina" (sem ligação conosco)

Estes são os principais projetos cripto com "Lumina" no nome que você pode encontrar:

### 1. Lumina Coin / Lumina Token

Existem vários tokens ERC-20 chamados "Lumina" ou "LUMI" em diversas blockchains. São **criptomoedas especulativas** emitidas por projetos de terceiros.

⚠️ **Atenção**: alguns desses tokens sofreram rug pull (golpes em que a equipe desaparece com a liquidez). Verifique sempre a equipe, o contrato e a liquidez **antes de investir**.

**Ligação com a Lumina Wallet: NENHUMA.** Não emitimos nenhum token.

### 2. Lumina Pay

Algumas plataformas usam o nome "Lumina Pay" para serviços de pagamento em cripto ou neobanking. Mais uma vez, **nenhuma ligação com a Lumina Wallet**.

### 3. Lumina Chain / Lumina Base

Há projetos que se apresentam como blockchains layer-1 ou rollups chamados "Lumina Chain" ou "Lumina Base". **Nenhuma ligação** com a nossa carteira — não desenvolvemos uma blockchain.

### 4. Lumina Crest AI

⚠️ **Alerta especial sobre este.** Vários relatos circulam sobre a Lumina Crest AI envolvendo práticas suspeitas (rendimentos garantidos, saques bloqueados). Pesquise "Lumina Crest AI review" no Google antes de se envolver com esse projeto.

**A Lumina Wallet não tem absolutamente nada a ver com a Lumina Crest AI.**

## Como verificar se você está usando a verdadeira Lumina Wallet

Para ter certeza de que está usando nosso aplicativo legítimo e não uma imitação:

### ✅ Verificações a fazer:

1. **Único site oficial**: [https://luminawallet.org](https://luminawallet.org)
   - Nenhum outro domínio representa a Lumina Wallet
2. **Google Play**: nome do desenvolvedor = *Lumina Wallet*, pacote = \`com.luminawallet.app\`
3. **App Store**: ID do aplicativo = \`id6760651380\`, editor = *Lumina Wallet*
4. **Twitter/X oficial**: [@getluminawallet](https://x.com/getluminawallet)
5. **Todas as nossas redes sociais** estão listadas na nossa [página Sobre](/en/about)

### 🚨 Sinais de alerta (golpe):

- Um site que pede sua seed phrase para "conectar" sua carteira
- Um "airdrop Lumina" que exige o pagamento antecipado de uma "taxa de gas"
- Um "suporte Lumina" que entra em contato por mensagem direta no Telegram ou Discord
- Um projeto que promete rendimentos com um "Lumina Token"
- Um site parecido com o nosso, mas com URL diferente (por exemplo, \`lumina-wallet.org\` com hífen, \`luminawallet.com\`, \`luminawallets.org\`)

## Por que essa confusão existe

O nome "Lumina" (do latim *lumen* — luz) é popular no mundo cripto porque evoca clareza e transparência. Por isso, **várias equipes independentes** adotaram esse nome sem qualquer coordenação.

Na **Lumina Wallet**, escolhemos esse nome para ilustrar nossa missão: **tornar a segurança cripto clara e luminosa**, com um scanner de golpes que traz à luz os riscos ocultos antes de cada transação.

## Conclusão

Se você procurava o token "Lumina Coin", "Lumina Pay" ou qualquer outra criptomoeda/plataforma com esse nome: **não somos nós e não temos nenhuma ligação com eles**. Faça sua própria pesquisa (DYOR) sobre esses projetos de terceiros.

Se você procura uma **carteira cripto móvel multi-chain non-custodial com proteção antigolpes integrada**, publicada por uma **empresa francesa transparente e registrada** — você está no lugar certo.

**Conheça a Lumina Wallet, a única e verdadeira:**

[Baixar no Google Play](https://play.google.com/store/apps/details?id=com.luminawallet.app) | [Baixar na App Store](https://apps.apple.com/us/app/lumina-wallet/id6760651380)`
  },
  "best-non-custodial-crypto-wallet-2026": {
    title: "Melhor Carteira Cripto Não Custodial em 2026: A Comparação Completa",
    meta: "Compare as melhores carteiras cripto não custodiais de 2026: Lumina Wallet, MetaMask, Trust Wallet, Rabby, Rainbow, Coinbase Wallet e Ledger. Funcionalidades, segurança, suporte multi-chain e proteção antigolpes comparados lado a lado.",
    body: `Escolher a **melhor carteira cripto não custodial** em 2026 é mais difícil do que nunca. Existem dezenas de opções — mobile, extensão de navegador, hardware — e cada uma afirma ser a mais segura, a mais fácil ou a mais completa. Este guia corta o ruído com uma **comparação lado a lado** das 7 carteiras não custodiais mais populares, com base em funcionalidades reais, segurança real e experiência de utilizador real.

## O Que É uma Carteira Não Custodial? (Revisão em 30 Segundos)

Uma [carteira não custodial](/en/blog/what-is-non-custodial-wallet) é uma carteira cripto em que **você** — e só você — controla as chaves privadas. Nenhuma exchange, nenhum banco, nenhum terceiro pode congelar, apreender ou perder os seus fundos. A contrapartida: você é 100% responsável pela sua frase-semente (seed phrase).

As carteiras não custodiais existem em três variantes:
- **Mobile** (Lumina, Trust Wallet, Rainbow) — Ideal para o uso diário
- **Extensão de navegador** (MetaMask, Rabby) — Ideal para utilizadores avançados de DeFi
- **Hardware** (Ledger, Trezor) — Ideal para armazenamento a frio de longo prazo

## As 7 Melhores Carteiras Não Custodiais em 2026 — Lado a Lado

| Carteira | Tipo | Chains | Scanner antigolpes | Filtro de tokens falsos | Ideal para |
|---|---|---|---|---|---|
| **Lumina Wallet** | Mobile | 10+ (BTC, ETH, SOL, XRP, BNB, POL...) | ✅ Integrado, em tempo real | ✅ Whitelist por contrato | Utilizadores do dia a dia que querem **segurança sem complexidade** |
| **MetaMask** | Navegador + Mobile | Redes EVM, Solana e Bitcoin | ⚠️ Avisos básicos | ❌ Mostra todos os tokens | Utilizadores avançados de DeFi |
| **Trust Wallet** | Mobile | 70+ | ⚠️ Scanner de terceiros | ❌ Mostra todos os tokens | Utilizadores multi-chain ocasionais |
| **Rabby** | Navegador | Ecossistema Ethereum | ✅ Simulação de transações | ❌ Mostra todos os tokens | DeFi avançado com pré-visualização de transações |
| **Rainbow** | Mobile | Ecossistema Ethereum | ⚠️ Avisos básicos | ❌ Mostra todos os tokens | Utilizadores apenas de Ethereum, ótima UX |
| **Coinbase Wallet** | Mobile + Navegador | 20+ | ⚠️ Avisos básicos | ❌ Mostra todos os tokens | Utilizadores da exchange Coinbase |
| **Ledger (com Ledger Live)** | Hardware | 100+ | ❌ Sem scanner | ❌ Mostra todos os tokens | Armazenamento a frio de longo prazo (>10k€) |

## Análise Detalhada: Cada Carteira em Pormenor

### 🥇 Lumina Wallet — A Melhor para Utilizadores do Dia a Dia Focados em Segurança

**Destaques:**
- ✅ Não custodial, chaves encriptadas com iOS Secure Enclave / Android Keystore
- ✅ **Scanner antigolpes de três camadas** (leitor de QR, auditoria de endereços, decodificador de transações)
- ✅ **Filtro de tokens falsos** — só aparecem na sua carteira os contratos oficiais
- ✅ Suporte multi-chain: Bitcoin, Ethereum, Solana, XRP, BNB Chain, Polygon, Arbitrum, Optimism, Base, Avalanche
- ✅ Assinaturas ERC-20 Permit e Permit2 decodificadas em linguagem simples
- ✅ Livro de endereços para evitar erros de digitação e ataques de copiar-colar
- ✅ Inteligência de ameaças GoPlus Security em tempo real
- ✅ **Empresa francesa** (HEDONISME SARL, RCS Dunkerque) — propriedade transparente

**Pontos fracos:**
- Ainda não é open-source (planeado)
- Apenas mobile por enquanto (sem extensão de navegador)

**Ideal para:** Quem quer **segurança máxima sem se tornar um especialista em cripto**. Sobretudo iniciantes e utilizadores de nível intermédio.

**Veredito:** ⭐⭐⭐⭐⭐

### 🥈 MetaMask — A Escolha dos Utilizadores Avançados de DeFi

**Destaques:**
- ✅ A carteira com maior compatibilidade entre aplicações Web3
- ✅ Disponível como extensão de navegador E como aplicação mobile
- ✅ Ecossistema Snaps para extensibilidade
- ✅ Open-source

**Pontos fracos:**
- ⚠️ Mostra todos os tokens por defeito → tokens falsos poluem a sua carteira
- ⚠️ Os avisos de assinatura são básicos e facilmente ignorados
- ⚠️ A Consensys (empresa-mãe) já foi criticada pela recolha de dados

**Ideal para:** Utilizadores avançados de DeFi que sabem o que estão a fazer.

**Veredito:** ⭐⭐⭐⭐

### 🥉 Trust Wallet — O Suporte de Chains Mais Amplo

**Destaques:**
- ✅ Mais de 70 blockchains suportadas (o mais amplo da categoria)
- ✅ Agregador de DEX integrado
- ✅ Pertence à Binance (equipa grande, bem financiada)

**Pontos fracos:**
- ⚠️ Proteção antigolpes básica (sobretudo reativa, não proativa)
- ⚠️ Mostra todos os tokens → risco de spam de tokens falsos
- ⚠️ Incidentes históricos (vulnerabilidade do WalletConnect em 2023, fragilidade do MPC divulgada)

**Ideal para:** Utilizadores ativos em chains pouco conhecidas.

**Veredito:** ⭐⭐⭐

### Rabby — A Melhor Pré-Visualização de Transações da Categoria

**Destaques:**
- ✅ Simula cada transação antes de assinar (mostra a alteração exata de saldo)
- ✅ Open-source
- ✅ Troca de chain sem adicionar redes manualmente

**Pontos fracos:**
- ⚠️ Apenas navegador (sem mobile)
- ⚠️ Apenas ecossistema Ethereum
- ⚠️ Curva de aprendizagem mais acentuada

**Ideal para:** Utilizadores avançados de DeFi que querem ver o impacto antes de assinar.

**Veredito:** ⭐⭐⭐⭐

### Rainbow — A Melhor UX (Mas Apenas Ethereum)

**Destaques:**
- ✅ Interface bonita e intuitiva
- ✅ Ótima integração para iniciantes
- ✅ Open-source

**Pontos fracos:**
- ⚠️ Apenas ecossistema Ethereum
- ⚠️ Funcionalidades antigolpes limitadas
- ⚠️ Problema de spam de tokens falsos

**Ideal para:** Utilizadores ocasionais apenas de Ethereum que valorizam a estética.

**Veredito:** ⭐⭐⭐

### Coinbase Wallet — A Ponte com a Exchange

**Destaques:**
- ✅ Integração perfeita com a exchange Coinbase
- ✅ Suporte multi-chain
- ✅ Marca de confiança

**Pontos fracos:**
- ⚠️ Funcionalidades antigolpes básicas
- ⚠️ Preocupações com a recolha de dados (alinhamento regulatório dos EUA)
- ⚠️ Mostra todos os tokens

**Ideal para:** Utilizadores da exchange Coinbase em transição para a autocustódia.

**Veredito:** ⭐⭐⭐

### Ledger + Ledger Live — A Melhor para Armazenamento de Longo Prazo

**Destaques:**
- ✅ Isolamento por hardware — as chaves nunca tocam na internet
- ✅ Mais de 100 chains suportadas
- ✅ A melhor solução para grandes montantes (>10 000 €)

**Pontos fracos:**
- ⚠️ O dispositivo custa entre 79 € e 249 €
- ⚠️ Dispositivo físico para transportar / perder
- ⚠️ Violação de dados em 2020 (e-mails de clientes divulgados)
- ⚠️ Sem scanner antigolpes — continua vulnerável a assinaturas maliciosas

**Ideal para:** Detentores de grandes quantias como cofre de poupança.

**Veredito:** ⭐⭐⭐⭐ (apenas para holdings de longo prazo)

## Qual Carteira Não Custodial É a Certa para SI?

Faça a si próprio estas 3 perguntas:

### 1. Quanto cripto possuo?
- **< 1 000 €** → **Lumina Wallet** (mobile, segura, gratuita)
- **1 000 € – 10 000 €** → **Lumina Wallet** para o uso diário + Ledger opcional para poupança
- **> 10 000 €** → **Ledger** para armazenamento a frio + Lumina Wallet para transações diárias

### 2. Sou ativo em DeFi?
- **Não, só guardo e envio** → **Lumina Wallet**
- **Sim, mas ocasionalmente** → **Lumina Wallet** (WalletConnect integrado)
- **Sim, utilizador avançado de DeFi diário** → **Rabby** ou MetaMask

### 3. Preocupo-me com golpes?
- **Sim, já ouvi histórias assustadoras** → **Lumina Wallet** (scanner de três camadas)
- **Sou cuidadoso e técnico** → MetaMask, Rabby ou Trust Wallet
- **Só envio para endereços conhecidos** → Qualquer carteira serve

## O Problema dos Tokens Falsos: Uma Diferença Crítica

A maioria das carteiras desta lista tem uma falha gritante: mostram **todos os tokens** que a blockchain lhes envia. Isto significa que, quando os burlões enviam por airdrop 10 000 "USDT" falsos para o seu endereço, MetaMask, Trust Wallet, Rainbow e Coinbase Wallet **exibem-nos todos** como se fossem reais.

Se clicar para vender — assina um contrato malicioso. Boom, carteira esvaziada.

**A Lumina Wallet é diferente.** Só exibe tokens de uma **whitelist estrita de endereços de contratos oficiais por blockchain**. USDT falso na Ethereum? Ignorado. USDC falso na BNB Chain? Ignorado. Só isto elimina o golpe cripto n.º 1 de 2026.

Leia o nosso guia completo: [Tokens falsos — como a Lumina os bloqueia na origem](/en/security).

## FAQ sobre Carteiras Não Custodiais

### Qual é a carteira não custodial mais segura em 2026?

Para a maioria dos utilizadores, a **Lumina Wallet** — porque combina autocustódia com proteção antigolpes ativa. Para grandes holdings de longo prazo, a **carteira de hardware Ledger**.

### Uma carteira não custodial pode ser hackeada?

A carteira em si, não — as chaves privadas ficam no seu dispositivo. Mas VOCÊ pode ser enganado a assinar uma transação maliciosa. É por isso que escolher uma carteira com **scanner de transações** é importante.

### A Lumina Wallet é mesmo gratuita?

Sim. Sem mensalidades, sem planos premium. A Lumina ganha dinheiro através de comissões transparentes de parceiros em serviços opcionais (staking via Everstake, compra de cripto via MoonPay/Transak) — claramente divulgadas.

### Devo usar várias carteiras?

Sim. O padrão dos detentores de cripto sérios: **Ledger para poupança** (>90% dos holdings) + **Lumina Wallet para uso diário** (<10%). Cada carteira tem a sua própria frase-semente.

## Conclusão: O Panorama das Carteiras Amadureceu

Em 2026, "não custodial" não chega. Toda a carteira séria é não custodial. O **verdadeiro diferenciador** é a proteção antigolpes: com que eficácia a carteira o defende dos seus próprios momentos de distração?

A nossa recomendação:
- **Utilizadores do dia a dia** → [Lumina Wallet](/en/download) (melhor relação segurança/usabilidade)
- **Utilizadores avançados de DeFi** → Rabby ou MetaMask
- **Grandes holdings** → combinação Ledger + Lumina Wallet

**Pronto para experimentar a carteira não custodial mais segura de 2026?**

[Descarregar a Lumina Wallet no Google Play](https://play.google.com/store/apps/details?id=com.luminawallet.app) | [Descarregar na App Store](https://apps.apple.com/us/app/lumina-wallet/id6760651380)`
  },
  "permit-malveillant-erc20-scam": {
    title: "Permit ERC-20 Malicioso: O Golpe Silencioso que Esvazia a Sua Carteira Sem Nenhuma Transação",
    meta: "Entenda como funcionam os permits ERC-20 maliciosos e as assinaturas Permit2 — o golpe cripto mais perigoso de 2026, que esvazia a sua carteira sem qualquer transação visível na blockchain. Saiba como detetá-los e bloqueá-los.",
    body: `Em 2026, mais de **500 milhões de dólares** foram roubados a utilizadores de cripto através de uma única técnica: o **permit ERC-20 malicioso**. Este golpe é particularmente perigoso porque não deixa **nenhum rasto visível na blockchain** quando você assina — apenas uma assinatura discreta que autoriza um atacante a esvaziar a sua carteira quando quiser.

Este guia explica o que é um permit malicioso, por que razão é o golpe favorito dos drainers em 2026 e, acima de tudo, **como se proteger**.

## O Que É um Permit ERC-20?

O **permit** é uma extensão do padrão ERC-20 introduzida em 2020 (EIP-2612). Permite a um utilizador **conceder autorização** a um smart contract para gastar os seus tokens **sem ter de enviar uma transação on-chain**.

Em linguagem simples: em vez de uma transação clássica de "Approve" que custa gas, você **apenas assina uma mensagem** com a sua carteira. Essa mensagem é depois submetida on-chain pelo próprio contrato.

**Benefícios legítimos:**
- ✅ Poupança de gas (uma transação em vez de duas)
- ✅ Melhor UX (o utilizador assina uma vez e tudo acontece)
- ✅ Utilizado pela Uniswap, 1inch e agregadores de DEX

**O problema:** esta assinatura é **invisível para a maioria das carteiras**. Você clica em "Assinar" pensando que está apenas a ligar-se a um site — e acabou de autorizar o atacante a esvaziar 100 000 USDC.

## Como o Golpe Funciona em 5 Passos

### Passo 1 — A isca
Você clica num anúncio do Google ou num link do Discord que leva a uma **falsa Uniswap**, a um **falso airdrop de cripto** ou a um **falso site de NFT**. O site parece 100% legítimo — logótipos oficiais, design profissional, URL enganador (\`uniswaap.org\` em vez de \`uniswap.org\`).

### Passo 2 — O pedido de ligação
O site pede-lhe que "ligue a sua carteira". Você clica, o MetaMask abre e vê apenas "Assinar uma mensagem". Nada de suspeito **à superfície**.

### Passo 3 — A mensagem oculta
O que você não vê: a mensagem contém parâmetros de permit ERC-20:
- **owner**: o seu endereço
- **spender**: o endereço do atacante
- **value**: montante máximo (normalmente 115 792 089 237 316 195... = infinito)
- **deadline**: 30 dias a partir de agora
- **nonce**: o seu nonce atual

Você assina. Nada acontece on-chain **de imediato**. Pensa que não aconteceu nada.

### Passo 4 — A exploração
Horas ou dias depois, o atacante submete **a sua assinatura** on-chain através de uma transação \`permit()\` + \`transferFrom()\`. O smart contract aceita porque a assinatura é matematicamente válida.

### Passo 5 — O roubo
Os seus USDC, USDT, WETH ou qualquer outro token ERC-20 são **transferidos para o endereço do atacante**. Você descobre a perda na manhã seguinte.

**Sem recurso possível.** A transação é irreversível. Nenhuma exchange centralizada está envolvida.

## Permit2: A Versão Agravada

O **Permit2** é uma extensão proposta pela Uniswap em 2022 para padronizar os permits em **todos os tokens ERC-20**, mesmo naqueles que não implementam nativamente o EIP-2612.

É **tecnicamente brilhante** para a UX de DeFi — e **catastrófico** para a segurança, porque:
- Uma única assinatura Permit2 pode autorizar o gasto de **vários tokens de uma só vez**
- A assinatura permanece válida durante **dias ou semanas**
- A maioria das carteiras apresenta o Permit2 como uma simples "mensagem", sem detalhes

**Resultado:** os drainers modernos usam o Permit2 como prioridade, porque torna o roubo **mais eficiente e mais discreto**.

## Como Detetar um Permit Malicioso

### Sinal de alerta n.º 1: O formato da assinatura
Uma assinatura Permit ou Permit2 aparece sempre na sua carteira como uma **mensagem tipada EIP-712** com esta estrutura:

\`\`\`
Domain: PermitToken or Permit2
Types:
  Permit: [owner, spender, value, nonce, deadline]
Message:
  owner: 0xYOUR_ADDRESS
  spender: 0xUNKNOWN  ← THIS is suspicious
  value: 115792089237316195423570985008687907853269984665640564039457584007913129639935  ← That's infinity!
  deadline: 1735689600
\`\`\`

### Sinal de alerta n.º 2: O contexto
- Acabou de chegar a um site **que nunca tinha usado** → 🔴
- O site oferece um **airdrop gratuito** ou um **rendimento anormal** → 🔴
- O site pede-lhe que assine **antes mesmo de mostrar conteúdo** → 🔴
- O URL tem um **erro de digitação subtil** (uniswaap em vez de uniswap) → 🔴

### Sinal de alerta n.º 3: Spender desconhecido
O campo \`spender\` (o contrato que vai gastar os seus tokens) deve ser um contrato **conhecido e verificado**. Se for um endereço aleatório que não aparece no Etherscan como contrato verificado, **é um golpe.**

## Como a Lumina Wallet Protege-o

A Lumina Wallet descodifica **todas** as assinaturas Permit e Permit2 antes de lhe pedir que assine. Eis o que acontece, concretamente:

1. **Descodificação da assinatura**: a Lumina analisa a mensagem EIP-712 e identifica se se trata de um permit
2. **Verificação do spender**: o contrato de destino é uma DEX/protocolo conhecido (Uniswap, 1inch, Aave...) ou um endereço desconhecido?
3. **Alerta de montante**: se o montante for "infinito" (2^256 - 1), surge um alerta crítico
4. **Tradução em linguagem simples**: em vez de um bloco hexadecimal, você vê: *"Está a autorizar [contrato desconhecido] a gastar [todos os seus USDC], sem limite, durante os próximos 30 dias"*

Resultado: você vê **exatamente** o que está a assinar antes de assinar. Um permit malicioso torna-se impossível de validar por engano.

## O Que Fazer Se Já Assinou um Permit Malicioso

### Passo 1 — Não entre em pânico, aja depressa
O permit pode ainda não ter sido explorado. Você tem **uma janela de tempo** antes de o atacante usar a sua assinatura.

### Passo 2 — Esvazie os tokens em risco
Transfira imediatamente todos os seus tokens ERC-20 para uma **nova carteira limpa**. Utilize uma carteira nova, com uma nova seed phrase. Não reutilize a carteira comprometida.

### Passo 3 — Revogue as aprovações existentes
Aceda ao [revoke.cash](https://revoke.cash) e revogue todas as aprovações. **Nota importante:** o revoke.cash NÃO consegue revogar uma assinatura Permit off-chain que ainda não tenha sido explorada. Só pode mover os tokens.

### Passo 4 — Denuncie o site fraudulento
- ScamAdviser
- Google Safe Browsing
- Chainabuse.com

### Passo 5 — Abandone a carteira comprometida
Nunca reutilize uma carteira que tenha assinado um permit malicioso, mesmo que não tenha perdido nada. Dependendo do contexto, o nonce da sua assinatura ainda pode ser explorado.

## O Consenso dos Especialistas

- **Vitalik Buterin (cofundador do Ethereum)** alertou publicamente para os riscos do Permit2 no X em 2024.
- A **Chainalysis** identificou a "assinatura de permit malicioso" como o **método de roubo cripto n.º 1 em 2025**, ultrapassando as aprovações ERC-20 clássicas.
- A **PeckShield** documentou mais de 300 ataques com permits maliciosos só em 2025.

## Conclusão: A Assinatura Invisível É o Novo Perigo

A era das transações visíveis on-chain acabou. Em 2026, o golpe mais perigoso não deixa **nenhum rasto visível** quando você valida. Uma assinatura off-chain, silenciosa, diferida — e a sua carteira fica vazia no dia seguinte.

**A única defesa eficaz:** uma carteira que **descodifique todas as assinaturas em linguagem simples** antes de você as validar. É exatamente isso que a Lumina Wallet faz — para Permit, Permit2 e todas as assinaturas EIP-712 maliciosas.

**Proteja-se agora:**

[Descarregar a Lumina Wallet na Google Play](https://play.google.com/store/apps/details?id=com.luminawallet.app) | [Descarregar na App Store](https://apps.apple.com/us/app/lumina-wallet/id6760651380)`
  },
  "how-to-set-up-non-custodial-wallet-2026": {
    title: "Como Configurar uma Carteira Cripto Não Custodial em 2026: Guia Completo Passo a Passo",
    meta: "Aprenda exatamente como configurar uma carteira cripto não custodial em 2026 — tutorial completo passo a passo sobre escolha da carteira, backup da seed phrase, configurações de segurança e sua primeira transação. Ideal para iniciantes.",
    body: `Configurar uma **carteira cripto não custodial** é o passo mais importante da sua jornada no mundo cripto. Faça isso corretamente e você será realmente dono das suas criptomoedas — nenhum banco, nenhuma exchange, nenhum terceiro poderá congelar ou perder os seus fundos. Faça de forma errada e você corre o risco de perder tudo por causa de um único erro.

Este guia passo a passo conduz você por **todo o processo** em 2026, desde a escolha da carteira certa até a sua primeira transação segura. Sem jargão, sem atalhos.

## Antes de Começar: As 3 Regras de Ouro

### Regra 1 — Nunca compartilhe a sua seed phrase
A sua seed phrase de 12 ou 24 palavras é a chave mestra de toda a sua carteira. **Nenhuma pessoa, aplicativo ou site legítimo jamais vai pedi-la.** Nem mesmo os desenvolvedores da carteira que você está usando. Se alguém pedir, é 100% golpe.

### Regra 2 — Nunca armazene a sua seed phrase de forma digital
Sem fotos. Sem capturas de tela. Sem notas na nuvem. Sem gerenciadores de senhas conectados à internet. Nunca. Escreva-a em **papel** ou grave-a em **metal**. Guarde o papel em um local fisicamente seguro.

### Regra 3 — Baixe sempre pelo site oficial
Aplicativos falsos de carteira na App Store e no Google Play roubam seed phrases assim que você as digita. Baixe apenas pelo **site oficial** da carteira (por exemplo, https://luminawallet.org), nunca pelos resultados de busca do Google Play ou da App Store.

## Passo 1 — Escolha a Carteira Não Custodial Certa

Para iniciantes em 2026, recomendamos a **Lumina Wallet** porque:
- ✅ Não custodial por design (você detém as chaves)
- ✅ Scanner antigolpe integrado (protege você dos seus próprios erros)
- ✅ Multi-chain desde o primeiro dia (Bitcoin, Ethereum, Solana, XRP, BNB Chain, Polygon...)
- ✅ Download gratuito
- ✅ Empresa francesa (HEDONISME SARL) com propriedade transparente

Alternativas a considerar de acordo com o seu perfil:
- **MetaMask** — Se você é um usuário avançado de DeFi
- **Ledger** — Se você mantém mais de 10 000 € no longo prazo (hardware wallet)
- **Rabby** — Se você usa DeFi diariamente no navegador

Veja nossa [comparação das melhores carteiras não custodiais de 2026](/en/blog/best-non-custodial-crypto-wallet-2026).

## Passo 2 — Baixe o Aplicativo pela Fonte Oficial

Para a Lumina Wallet:
1. Abra o navegador do seu celular (Safari no iOS, Chrome no Android)
2. Digite **luminawallet.org** diretamente (NÃO pesquise no Google — os anúncios do Google podem ser falsos)
3. Clique no botão **"Download"**
4. Você será redirecionado para a página oficial da Google Play Store ou da App Store

Verifique antes de instalar:
- ✅ Nome do desenvolvedor: **Lumina Wallet**
- ✅ ID na App Store: **id6760651380**
- ✅ Pacote no Google Play: **com.luminawallet.app**
- ✅ Número de downloads > 1 000 (clones falsos têm pouquíssimos downloads)

Depois de verificar a identidade, instale o aplicativo.

## Passo 3 — Crie uma Nova Carteira (Não Importe)

Abra o aplicativo pela primeira vez. Você verá duas opções:
- **"Create New Wallet"** ← Escolha esta
- **"Import Existing Wallet"** ← Somente se você já tiver uma seed phrase

Toque em **Create New Wallet**. O aplicativo gera as suas chaves privadas **localmente no seu dispositivo**. Elas nunca passam pelos nossos servidores, pelos nossos desenvolvedores nem pela internet.

## Passo 4 — Faça o Backup da Sua Seed Phrase (O Passo Crítico)

O aplicativo agora exibirá **12 ou 24 palavras** em uma ordem específica. Essa é a sua **seed phrase de recuperação**.

### Como fazer o backup corretamente:

1. **Pegue caneta e papel** (não o celular).
2. **Escreva as palavras em ordem**, uma por linha, com o número ao lado de cada uma.
3. **Confira cada palavra duas vezes** — um único erro de digitação significa que você não conseguirá recuperar a sua carteira.
4. **Verifique a grafia** comparando com o aplicativo, uma palavra de cada vez.
5. **Guarde o papel** em um local fisicamente seguro: uma gaveta trancada, um cofre em casa, um cofre de banco.
6. **Faça uma segunda cópia** e guarde-a em um **local físico diferente** (por exemplo, com um familiar de confiança).
7. **Nunca fotografe** o papel. Nunca o guarde em Notas, iCloud, Google Drive ou qualquer serviço de nuvem.

**Configuração avançada opcional:** grave a sua seed phrase em aço inoxidável (usando kits como Cryptosteel, Billfodl ou um simples conjunto de punções de metal). Resistente a fogo, água e ao tempo.

### O que NÃO fazer:
- ❌ Tirar uma foto "só para backup"
- ❌ Enviá-la para si mesmo por e-mail
- ❌ Guardá-la em um gerenciador de senhas online
- ❌ Salvá-la no aplicativo de Notas do celular
- ❌ Compartilhá-la com qualquer pessoa (cônjuge, família, "agentes de suporte")

## Passo 5 — Confirme a Sua Seed Phrase

O aplicativo pedirá que você **verifique** se anotou a frase corretamente. Ele mostra algumas posições (por exemplo, palavra nº 3, palavra nº 7, palavra nº 11) e pede que você toque na palavra correta em uma lista.

Se você errar, o aplicativo não bloqueia o acesso — mas é um forte sinal de que você não fez o backup corretamente. **Refaça o backup** até conseguir confirmar a partir do seu papel.

## Passo 6 — Defina uma Senha Forte para a Carteira

Crie uma **senha forte** para desbloquear o aplicativo no seu dispositivo:
- Pelo menos **14 caracteres**
- Combinação de maiúsculas, minúsculas, números e símbolos
- **Diferente de todas as outras senhas que você usa**
- Guarde-a em um gerenciador de senhas (Bitwarden, 1Password, KeePass)

Essa senha criptografa as suas chaves localmente. Mesmo que alguém roube o seu celular, não conseguirá abrir a Lumina Wallet sem ela.

## Passo 7 — Ative a Biometria + PIN

No menu de Configurações da sua carteira, ative:
- ✅ **Face ID** ou **Impressão digital** (biometria do iOS/Android)
- ✅ **Código PIN** (6 dígitos, diferente do PIN de desbloqueio do celular)

Agora você tem **3 camadas de autenticação** protegendo a sua carteira:
1. Desbloqueio do celular (Face ID / PIN)
2. PIN da carteira (6 dígitos)
3. Senha da carteira (14+ caracteres)

Mesmo que um ladrão roube o seu celular desbloqueado, ele não conseguirá acessar as suas criptomoedas.

## Passo 8 — Verifique Tudo com uma Pequena Transação de Teste

Antes de armazenar qualquer quantia significativa:

1. Envie para si mesmo **5 € a 10 € em cripto** a partir de uma exchange (Coinbase, Binance, Kraken) para o endereço da sua nova carteira.
2. Aguarde a confirmação da transação (geralmente de 1 a 15 minutos, dependendo da blockchain).
3. Envie o valor **de volta** para a exchange como um novo teste.

Se ambos funcionarem, a sua carteira está configurada corretamente. **Nunca armazene grandes quantias antes de fazer esse teste.**

## Passo 9 — Pratique com o Scanner Antigolpe

A Lumina Wallet tem um scanner integrado que protege você contra golpes comuns. Teste-o:

- **Tente enviar para um endereço obviamente falso** (por exemplo, 0x000...000) → a Lumina bloqueará
- **Tente escanear um QR code que não seja relacionado a cripto** → a Lumina rejeitará
- **Observe a prévia da transação** antes de assinar → a Lumina mostra exatamente quais tokens estão saindo

Familiarizar-se com esses alertas **agora** significa que você os reconhecerá quando um golpe real acontecer.

## Passo 10 — Só Agora, Armazene Quantias Reais

Depois de:
- ✅ Confirmar o backup da seed phrase
- ✅ Configurar biometria + PIN + senha
- ✅ Fazer com sucesso uma transação de teste
- ✅ Praticar com o scanner

...**então** você pode começar a armazenar quantias reais.

Padrão recomendado para investidores sérios:
- **Carteira do dia a dia**: mantenha aqui menos de 10 % do total das suas criptomoedas (para gastos, DeFi, swaps)
- **Cofre de poupança**: para grandes quantias, use uma hardware wallet (Ledger) com uma seed phrase diferente, armazenada offline

## Erros Comuns que Arruínam a Segurança da Carteira

Mesmo usuários que seguem este guia às vezes cometem estes erros. Evite-os:

- ❌ **Tirar captura de tela da seed phrase** "só para backup" — o iCloud a sincroniza com a nuvem
- ❌ **Reutilizar a mesma senha** na carteira e no e-mail
- ❌ **Clicar em anúncios do Google** de aplicativos de carteira (clones falsos usam anúncios do Google)
- ❌ **Ignorar alertas de segurança** por estar com pressa
- ❌ **Conectar-se a DApps aleatórios** para "testar" um airdrop

## Perguntas Frequentes sobre a Configuração de uma Carteira Não Custodial

### Quanto tempo leva para configurar uma carteira não custodial?

Se você seguir este guia com atenção, cerca de **15 a 20 minutos**. A maior parte desse tempo é gasta escrevendo e verificando corretamente a seed phrase.

### É seguro digitar a minha seed phrase no celular?

Somente no **cenário exato** em que você está configurando uma carteira NOVA ou recuperando uma existente **dentro do aplicativo oficial da carteira**. Nunca a digite em um site, em um chat de suporte ou em qualquer outro aplicativo.

### Posso usar a mesma seed phrase em várias carteiras?

Sim. A sua seed phrase é a chave mestra — você pode importá-la para qualquer carteira compatível com BIP-39 (Lumina, MetaMask, Trust Wallet, Ledger...) e acessar as mesmas contas. No entanto, a melhor prática é **usar seed phrases diferentes para finalidades diferentes** (dia a dia vs. poupança).

### E se eu perder o meu celular?

Se você tem a sua seed phrase anotada: instale a Lumina Wallet em um novo celular, escolha "Import Existing Wallet" e digite a sua seed phrase — os seus fundos estarão de volta instantaneamente.

Se você não tem a sua seed phrase anotada: as suas criptomoedas estão **perdidas para sempre**. Sem exceção.

## Conclusão: Configure Direito, Durma Tranquilo

Configurar uma carteira não custodial é um **investimento único** que determina a segurança das suas criptomoedas por anos. Dedique os 5 minutos extras para anotar a sua seed phrase corretamente, ativar todas as camadas de segurança e testar primeiro com pequenas quantias.

A **Lumina Wallet** foi projetada para tornar esse processo o mais seguro possível — com um scanner antigolpe que detecta erros que você possa cometer mais tarde, por mais cuidadoso que seja hoje.

**Pronto para configurar a sua primeira carteira não custodial?**

[Baixar a Lumina Wallet no Google Play](https://play.google.com/store/apps/details?id=com.luminawallet.app) | [Baixar na App Store](https://apps.apple.com/us/app/lumina-wallet/id6760651380)`
  },
  "unhosted-wallet-vs-custodial-wallet": {
    title: "Carteira Unhosted vs Carteira Custodial: O Guia Regulatório de 2026",
    meta: "O que é uma \"unhosted wallet\"? Entenda a diferença entre carteiras unhosted e custodiais — o termo do Tesouro dos EUA para carteiras non-custodial — e o que isso significa para a regulação cripto, a privacidade e a autocustódia em 2026.",
    body: `Se você tem acompanhado as publicações recentes do Tesouro dos EUA ou da FinCEN, provavelmente já se deparou com o termo **"unhosted wallet"** (carteira não hospedada). É o rótulo usado no jargão regulatório para aquilo que a comunidade cripto chama de **carteira non-custodial** — mas com nuances sutis que importam em 2026. Este guia esclarece o que é uma carteira unhosted, como ela se diferencia de uma carteira custodial e por que a terminologia importa para a privacidade, a conformidade e a autocustódia.

## O Que É uma Carteira Unhosted?

Uma **carteira unhosted** é uma carteira de criptomoedas em que as chaves privadas são controladas pelo usuário, e não por um prestador de serviços terceirizado. Na linguagem dos reguladores financeiros dos EUA — principalmente a FinCEN (Financial Crimes Enforcement Network) e o Tesouro dos EUA — "unhosted" significa: **não hospedada por uma instituição financeira regulada**.

**Concretamente, uma carteira unhosted é:**
- ✅ Uma carteira em que **você** detém as chaves privadas
- ✅ Não operada por um banco, uma exchange ou qualquer Money Services Business (MSB) registrada
- ✅ Uma carteira que você pode acessar sem passar por um processo de KYC

**Sinônimos comuns em 2026:**
- Carteira non-custodial (termo do setor)
- Carteira de autocustódia (termo educativo)
- Carteira pessoal (termo informal)

## Unhosted vs Custodial: As Principais Diferenças

| Característica | Carteira Unhosted | Carteira Custodial |
|---|---|---|
| **Quem detém as chaves privadas?** | Você | Um terceiro regulado (exchange, banco) |
| **KYC obrigatório?** | Não | Sim (verificação de identidade) |
| **As contas podem ser congeladas?** | Não — ninguém tem esse poder | Sim — o custodiante pode congelar mediante solicitação |
| **Reporte regulatório?** | Limitado (varia conforme a jurisdição) | Completo (cada transação é reportada às autoridades) |
| **Legalmente, quem é o dono dos fundos?** | Você diretamente | O custodiante os mantém em seu nome |
| **Risco de falência?** | Nenhum | Sim — você é um credor quirografário |
| **Exemplos** | Lumina Wallet, MetaMask, Ledger | Coinbase, Binance, Kraken (conta padrão) |

## Por Que os Reguladores Usam o Termo "Unhosted"

O termo "unhosted" apareceu de forma destacada em uma **proposta de regra da FinCEN em dezembro de 2020**, que teria exigido que as exchanges coletassem informações de identidade em transações de e para carteiras unhosted acima de determinados limites.

A regra foi:
- **Não adotada em sua forma original** (forte resistência do setor)
- Parcialmente implementada no **Crypto-Asset Reporting Framework dos EUA de 2024**
- Parte de um alinhamento mais amplo do G7/FATF sobre a **Travel Rule** para criptoativos

Em 2026, "unhosted wallet" é o termo mais usado em:
- Publicações do Tesouro dos EUA e da FinCEN
- Recomendações do FATF (Financial Action Task Force)
- Regulamento europeu MiCA (Markets in Crypto-Assets)
- Depoimentos no Congresso sobre política cripto

## Usar uma Carteira Unhosted É Legal?

**Sim.** Usar uma carteira unhosted é **totalmente legal** nos Estados Unidos, na União Europeia, no Reino Unido, na França e em praticamente todas as democracias em 2026.

O que é regulado:
- Transações **entre exchanges e carteiras unhosted** (sujeitas a limites de reporte)
- Certas atividades empresariais envolvendo carteiras unhosted (transmissão de dinheiro, serviços de custódia)
- **Não** a mera posse ou uso de uma carteira unhosted por um indivíduo

Para ser claro: **você pode legalmente possuir, usar, enviar e receber criptomoedas em uma carteira unhosted na França e na UE em 2026 — sem necessidade de licença ou autorização.**

## O Trade-Off da Privacidade

As carteiras unhosted oferecem **mais privacidade** do que as carteiras custodiais porque:
- Não é necessário KYC para criá-las
- Não há reporte de transações às autoridades centrais
- Não há bloqueio de transações por intermediários

No entanto, essa privacidade **não é absoluta**:
- As transações em blockchain são **publicamente visíveis para sempre**
- Se sua identidade for vinculada ao seu endereço (por meio de exchanges, dApps ou eventos de KYC), todo o seu histórico on-chain se torna rastreável
- **Empresas de análise de blockchain** (Chainalysis, Elliptic, TRM Labs) monitoram ativamente a atividade na blockchain

Para ter privacidade significativa em 2026, você precisa de:
- Uma carteira unhosted
- Uso cuidadoso de exchanges (usando rampas de dinheiro para cripto sempre que possível)
- Ferramentas focadas em privacidade (mixers, moedas de privacidade ou protocolos de privacidade ZK)

⚠️ **Observação:** As regulamentações sobre ferramentas de privacidade estão evoluindo. Consulte um advogado local em caso de uso de alto valor ou empresarial.

## Carteiras Unhosted e o MiCA (Europa)

O **regulamento MiCA da UE** (Markets in Crypto-Assets), plenamente aplicável desde dezembro de 2024, trata as carteiras unhosted da seguinte forma:

- **Indivíduos** podem usar livremente carteiras unhosted — nenhum reporte é exigido
- **Prestadores de Serviços de Criptoativos (CASPs)** — exchanges, custodiantes — devem coletar dados de identidade em transações acima de **1 000 €** de ou para carteiras unhosted ("Travel Rule")
- Transações entre carteiras unhosted de indivíduos permanecem **não reguladas**

Na prática: como usuário francês, você pode usar livremente a Lumina Wallet, receber criptomoedas nela e enviar a partir dela — sem nenhuma obrigação especial.

## Segurança da Carteira Unhosted: O Que Você Precisa Saber

Como as carteiras unhosted lhe dão controle total, elas também lhe dão **responsabilidade total**:

### O lado bom:
- ✅ **Nenhum terceiro** pode congelar seus fundos
- ✅ **Nenhuma falência de custodiante** pode fazer você perder seus fundos
- ✅ **Privacidade total** por padrão
- ✅ **Acesso direto ao DeFi** sem intermediários

### O trade-off:
- 🔴 **Perdeu a seed phrase = perdeu os fundos** (não existe "esqueci a senha")
- 🔴 **Caiu em phishing = perdeu os fundos** (nenhuma reversão é possível)
- 🔴 **Assinou um contrato malicioso = perdeu os fundos** (responsabilidade inteiramente sua)

É por isso que escolher uma carteira unhosted com **proteção antigolpe integrada** — como a Lumina Wallet — melhora significativamente a relação risco/retorno. Você mantém a liberdade da autocustódia e ainda ganha uma rede de segurança contra seus próprios erros.

## Você Deve Usar uma Carteira Unhosted?

**Sim, se:**
- Você quer a verdadeira propriedade das suas criptomoedas
- Você é sério sobre manter ativos no longo prazo
- Você quer usar DeFi, NFTs ou aplicativos Web3
- Você se preocupa com falência de exchanges ou congelamento de contas
- Você valoriza a privacidade

**Não, se:**
- Você só negocia ocasionalmente em exchanges centralizadas
- Você não se sente confortável em gerenciar uma seed phrase
- Você precisa da rede de segurança do "esqueci a senha" dos serviços custodiais
- A simplicidade do reporte regulatório importa mais para você do que a autocustódia

Para a maioria dos usuários sérios de cripto, a resposta é: **use ambas**. Mantenha os saldos de negociação ativa em uma exchange regulada e transfira as posições de longo prazo para uma carteira unhosted.

## FAQ: Carteiras Unhosted

### "Unhosted wallet" é o mesmo que "cold wallet"?

Não exatamente. **"Unhosted"** descreve quem controla as chaves (você, e não um terceiro). **"Cold"** descreve onde as chaves são armazenadas (offline vs online). Uma carteira pode ser unhosted E hot (Lumina, MetaMask), ou unhosted E cold (Ledger, Trezor).

### Preciso declarar minha carteira unhosted na declaração de impostos?

Na França, a partir de 2020, é necessário declarar as **contas de ativos digitais no exterior** no Formulário 3916-BIS. As carteiras unhosted mantidas diretamente (sem um custodiante) geralmente **não estão sujeitas a essa obrigação**, mas as regras de reporte estão evoluindo. Consulte um profissional tributário para o seu caso.

### O governo pode apreender criptomoedas em uma carteira unhosted?

Somente se tiver as chaves privadas ou o obrigar a entregá-las por meio de um processo legal. Há casos documentados de autoridades que apreenderam criptomoedas ao obter seed phrases por meio de mandados de busca ou acordos de delação. **Propriedade criptográfica não é o mesmo que imunidade jurídica.**

### Qual é a melhor carteira unhosted para iniciantes?

A **Lumina Wallet** foi projetada especificamente para usuários que querem autocustódia sem precisar se tornar especialistas em cripto. A proteção antigolpe integrada pega os erros mais comuns, enquanto o suporte multichain (Bitcoin, Ethereum, Solana, XRP, BNB, Polygon...) cobre tudo o que você realisticamente vai precisar.

## Conclusão

"Unhosted wallet" é a palavra do regulador para **carteira non-custodial** — uma ferramenta fundamental para a propriedade de criptoativos em 2026. Entender o termo ajuda você a acompanhar os debates de políticas públicas, as declarações fiscais e a conformidade na era do MiCA.

Resumindo: as carteiras unhosted são **legais, poderosas e vieram para ficar**. Escolha uma que combine autocustódia com proteção prática — como a Lumina Wallet — e você terá o melhor dos dois mundos.

**Pronto para experimentar uma carteira unhosted moderna?**

[Baixe a Lumina Wallet no Google Play](https://play.google.com/store/apps/details?id=com.luminawallet.app) | [Baixe na App Store](https://apps.apple.com/us/app/lumina-wallet/id6760651380)`
  },
  "non-custodial-wallet-app-2026-mobile-guide": {
    title: "App de Carteira Non-Custodial: O Guia Definitivo de 2026 para Utilizadores Mobile",
    meta: "À procura da melhor app de carteira non-custodial em 2026? Guia completo focado em mobile: o que esperar, requisitos essenciais de segurança, comparação das 5 melhores apps iOS/Android e como identificar clones falsos na App Store.",
    body: `Procurar uma **app de carteira non-custodial** em 2026 é confuso. Existem centenas de apps na App Store e no Google Play, e cada uma afirma ser a mais segura, a mais fácil ou a mais completa. Algumas são excelentes. Outras são verdadeiras burlas (clones falsos concebidos para roubar seed phrases). Este guia é 100% focado em mobile — sem extensões de navegador, sem hardware — e oferece um quadro prático para escolher a app certa E evitar as armadilhas.

## O Que É uma App de Carteira Non-Custodial?

Uma **app de carteira non-custodial** é uma aplicação móvel (iOS ou Android) em que:
- **Você** detém as chaves privadas — encriptadas localmente no seu telemóvel
- **Nenhuma exchange, nenhum banco, nenhum terceiro** pode congelar ou perder as suas criptomoedas
- **A sua seed phrase** é gerada localmente e nunca sai do seu dispositivo
- O acesso é protegido por biometria + PIN + palavra-passe

As melhores carteiras non-custodial para mobile em 2026 suportam mais de 10 blockchains, integram-se com apps Web3 através de WalletConnect e — acima de tudo — incluem **proteção anti-burla** integrada diretamente na app.

## Porque É Que o Mobile Importa Mais do Que o Desktop em 2026

Mais de **72% das transações cripto** em 2026 são feitas em dispositivos móveis (Chainalysis 2026 State of Crypto). As carteiras non-custodial mobile-first ultrapassaram as extensões de navegador porque:

- 🔐 Os telemóveis modernos têm **módulos de segurança de hardware** (iOS Secure Enclave, Android Keystore) que os navegadores não conseguem igualar
- 📱 A **autenticação biométrica** (Face ID, impressão digital) é um padrão
- 🚀 As **notificações push** avisam-no instantaneamente de transações recebidas
- 📷 A **câmara + leitura de QR** permite introduzir endereços de forma mais segura
- 🌐 O **WalletConnect v2** permite que as carteiras móveis assinem para dApps de desktop

## As 5 Melhores Apps de Carteira Non-Custodial em 2026

### 🥇 Lumina Wallet — A Melhor Carteira Mobile Focada em Segurança
- **Plataformas:** iOS + Android
- **Chains:** Bitcoin, Ethereum, Solana, XRP, BNB Chain, Polygon, Arbitrum, Optimism, Base, Avalanche
- **Funcionalidades exclusivas:** Scanner anti-burla de três camadas (leitor de QR, auditoria de endereços, descodificador de transações) + [filtro de whitelist de tokens falsos](/en/security)
- **Ideal para:** Utilizadores que querem proteção máxima sem se tornarem especialistas em cripto
- **Gratuita** — sem subscrição

### 🥈 Trust Wallet — Maior Suporte de Chains
- **Plataformas:** iOS + Android
- **Chains:** mais de 70
- **Ponto fraco:** Mostra todos os tokens por defeito (risco de spam de tokens falsos), proteção anti-burla básica
- **Ideal para:** Utilizadores ativos em muitas chains pouco conhecidas

### 🥉 MetaMask Mobile — O Padrão do DeFi
- **Plataformas:** iOS + Android
- **Chains:** Redes EVM, Solana e Bitcoin
- **Ponto fraco:** Avisos básicos, sem filtro de tokens falsos, fricção histórica na experiência de utilização
- **Ideal para:** Utilizadores já familiarizados com a MetaMask no navegador

### Rainbow — A Melhor UX para Ethereum
- **Plataformas:** iOS + Android
- **Chains:** Apenas ecossistema Ethereum
- **Ponto fraco:** Suporte multi-chain limitado
- **Ideal para:** Utilizadores casuais de Ethereum que valorizam o design

### Coinbase Wallet — A Melhor para Utilizadores da Coinbase
- **Plataformas:** iOS + Android
- **Chains:** mais de 20
- **Ponto fraco:** Anti-burla básico, preocupações com a recolha de dados
- **Ideal para:** Pessoas que estão a passar da exchange Coinbase para a autocustódia

## As 4 Funcionalidades Inegociáveis em 2026

Ao comparar apps de carteira non-custodial, exija TODAS AS QUATRO:

### 1. Armazenamento Local de Chaves com Segurança de Hardware
As chaves privadas devem ser encriptadas com o iOS Secure Enclave ou o Android Keystore. **Se a app guarda as chaves em texto simples ou com sincronização na cloud — afaste-se.**

### 2. Scanner Anti-Burla Integrado
Qualquer carteira de 2026 que se preze deve:
- Verificar os endereços dos destinatários em listas de endereços maliciosos conhecidos
- Descodificar assinaturas ERC-20 Permit / Permit2 em linguagem clara
- Simular transações antes de assinar (mostra exatamente que tokens saem)
- Alertar para padrões de "aprovação ilimitada"

### 3. Filtro de Tokens Falsos
Este é o **maior diferenciador de 2026**. Os burlões fazem airdrop de 10 000 tokens "USDT" falsos para milhões de carteiras. As carteiras que mostram todos os tokens são perigosas. Procure carteiras que usem uma **whitelist de endereços de contratos oficiais por blockchain** (a Lumina é atualmente a líder neste campo).

### 4. Multi-Chain Desde o Primeiro Dia
Uma única seed phrase deve gerar endereços para Bitcoin, Ethereum, Solana, XRP, BNB Chain e outras redes principais. Sem ter de gerir várias apps.

## Como Identificar Apps de Carteira Falsas na App Store

Os **clones falsos** são uma grande ameaça em 2026. Copiam o nome, o logótipo e a descrição da Lumina Wallet — e depois roubam a sua seed phrase no momento em que a introduz.

**Lista de verificação antes de descarregar:**

- ✅ Descarregue apenas a partir do **site oficial** (p. ex. https://luminawallet.org). Nunca pesquise na App Store.
- ✅ Verifique se o **nome do programador** corresponde exatamente (a Lumina Wallet é publicada por *Lumina Wallet*)
- ✅ Verifique o **ID do pacote**: a verdadeira Lumina = \`com.luminawallet.app\`
- ✅ Verifique o **ID da App Store**: a verdadeira Lumina = \`id6760651380\`
- ✅ Número de descarregamentos > 1 000 (os clones falsos costumam ter < 100)
- ✅ Leia as avaliações — os clones têm frequentemente avaliações de 5 estrelas suspeitamente uniformes

📖 Leia o nosso guia completo: [Clones Falsos de Carteiras Cripto](/en/blog/fake-crypto-wallet-clones)

## FAQ sobre Apps de Carteira Non-Custodial

### Uma app de carteira non-custodial é mais segura do que uma app de exchange?

Para armazenamento a médio e longo prazo, **sim**. Uma carteira non-custodial não pode ser hackeada ao nível da plataforma — cada utilizador tem chaves separadas em dispositivos separados. As apps de exchange são honeypots que os atacantes visam constantemente.

### As apps de carteira non-custodial custam dinheiro?

As melhores apps da categoria, como a **Lumina Wallet**, são 100% gratuitas. Algumas carteiras monetizam através de comissões transparentes de parceiros em serviços opcionais (staking, compra de cripto). Desconfie de apps que exigem uma "subscrição premium" para funcionalidades básicas de carteira.

### Posso usar a mesma app de carteira non-custodial em vários telemóveis?

Sim — instale a app em ambos os telemóveis e importe a sua seed phrase em cada um. Ambos mostrarão os mesmos saldos e poderão enviar transações de forma independente. Tenha cuidado: qualquer telemóvel com a sua seed phrase tem acesso total.

### O que acontece se o programador da app de carteira desaparecer?

As suas criptomoedas continuam seguras. Non-custodial significa que a app é apenas uma interface — as suas chaves estão na blockchain. Se a Lumina Wallet alguma vez desaparecesse, poderia importar a sua seed phrase para **qualquer carteira compatível com BIP-39** (MetaMask, Trust Wallet, Ledger…) e recuperar o acesso total.

## Conclusão: Escolha uma App de Carteira em que Possa Confiar durante Anos

A sua app de carteira non-custodial é onde vai interagir com as suas criptomoedas **centenas de vezes** nos próximos anos. Escolha uma que combine autocustódia com proteção ativa contra as ameaças de 2026 — assinaturas maliciosas, tokens falsos, endereços de phishing.

A **Lumina Wallet** foi criada precisamente para isto: uma carteira non-custodial mobile com scanner anti-burla de três camadas, exibição de tokens apenas de contratos oficiais e suporte para mais de 10 blockchains. Gratuita, transparente (empresa francesa, HEDONISME SARL, RCS Dunkerque 901 439 505), sem recolha de dados.

**Pronto para experimentar a app de carteira non-custodial mais segura de 2026?**

[Descarregar a Lumina Wallet no Google Play](https://play.google.com/store/apps/details?id=com.luminawallet.app) | [Descarregar na App Store](https://apps.apple.com/us/app/lumina-wallet/id6760651380)`
  },
  "metamask-vs-lumina-wallet-anti-scam-comparison": {
    title: "MetaMask vs Lumina Wallet: comparação antifraude 2026",
    meta: "MetaMask ou Lumina Wallet em 2026? Uma comparação lado a lado, brutalmente honesta, focada na proteção contra golpes: filtro de tokens falsos, decodificação de assinaturas, scanner de endereços, suporte multi-chain e UX mobile.",
    body: `A **MetaMask** é a carteira non-custodial mais popular do mundo, com mais de 30 milhões de usuários ativos por mês. A **Lumina Wallet** é uma concorrente mais recente, criada primeiro para o celular e desenvolvida especificamente para a proteção contra golpes. Qual delas você deve usar em 2026? Esta comparação lado a lado se concentra na ÚNICA coisa que mais importa no cenário cripto atual: o quanto cada carteira protege você contra o esvaziamento dos seus fundos.

## A resposta rápida

- **Escolha a MetaMask se:** você é um usuário experiente de DeFi, usa o navegador e sabe ler chamadas de funções em Solidity antes de assinar.
- **Escolha a Lumina Wallet se:** você quer uma carteira mobile que detecta golpes por você — antes que você cometa um erro caro.

Para a maioria dos usuários em 2026, a resposta é a **Lumina Wallet**. Veja por quê.

## Comparação lado a lado

| Recurso | MetaMask | Lumina Wallet |
|---|---|---|
| **Plataforma** | Navegador + Mobile | Somente mobile (iOS + Android) |
| **Redes suportadas** | Redes EVM, Solana e Bitcoin | Mais de 10, incluindo BTC, ETH, SOL, XRP, BNB |
| **Armazenamento de chaves** | Armazenamento do navegador / Keystore mobile | iOS Secure Enclave / Android Keystore |
| **Scanner antigolpe de endereços** | ⚠️ Básico (apenas lista de bloqueio) | ✅ GoPlus em tempo real + scanner próprio |
| **Filtro de tokens falsos** | ❌ Mostra todos os tokens | ✅ Lista branca por contrato oficial |
| **Decodificador de ERC-20 Permit** | ⚠️ Mostra dados hexadecimais brutos | ✅ Tradução em linguagem simples |
| **Decodificador de Permit2** | ⚠️ Básico | ✅ Decodificação completa com verificação de valor + spender |
| **Simulação de transações** | ⚠️ Básica (somente Ethereum) | ✅ Simulação multi-chain |
| **Agenda de endereços (anti-erro de digitação)** | ❌ Não | ✅ Sim |
| **Código aberto** | ✅ Sim | ⚠️ Ainda não (planejado) |
| **Transparência da empresa** | ⚠️ Consensys (EUA) | ✅ HEDONISME SARL (França) — RCS verificável |
| **Preço** | Grátis | Grátis |

## A maior diferença: o filtro de tokens falsos

Em 2026, o **golpe nº 1 em cripto** são os airdrops maliciosos. Golpistas criam tokens USDT, USDC ou WETH falsos, com o mesmo nome e logotipo dos verdadeiros, e depois enviam 10 000 deles para milhões de carteiras. Quando você clica para vender, assina um contrato malicioso → sua carteira é esvaziada.

### Como a MetaMask lida com isso
A MetaMask mostra **todos os tokens** que a blockchain envia para você. Se alguém fizer um airdrop de USDT falso para o seu endereço, ele aparece na sua carteira — mesmo nome, mesmo logotipo. É preciso verificar manualmente o endereço do contrato no Etherscan para confirmar que é verdadeiro. A maioria dos usuários não faz isso.

### Como a Lumina Wallet lida com isso
A Lumina usa uma **lista branca rigorosa de endereços de contratos oficiais por blockchain**. Somente o USDT do contrato oficial da Ethereum (0xdAC17F...) aparece. Tokens falsos ficam invisíveis. Nenhuma ação do usuário é necessária.

Leia nossa análise completa: [Como a Lumina bloqueia tokens falsos na origem](/en/security)

## Decodificação de assinaturas: a prevenção silenciosa contra golpes

As assinaturas maliciosas de ERC-20 Permit e Permit2 custaram aos usuários **mais de US$ 500 milhões em 2026** (veja nosso [guia completo](/en/blog/malicious-erc20-permit-scam)). Elas funcionam porque a maioria das carteiras exibe as assinaturas como dados hexadecimais opacos — os usuários não entendem o que estão assinando.

### A MetaMask e as assinaturas
A MetaMask mostra as assinaturas Permit como uma mensagem EIP-712 formatada, mas os detalhes são técnicos. Campos como \`spender\`, \`value\` e \`nonce\` são visíveis, mas não são explicados. Usuários casuais costumam deixar passar os alertas de "aprovação ilimitada".

### A Lumina Wallet e as assinaturas
A Lumina **decodifica cada assinatura** em linguagem simples antes de pedir que você assine:
- "Você está autorizando [nome do contrato / contrato desconhecido] a gastar [valor / ILIMITADO] do seu [token], por [duração]"
- Alertas críticos para valores ilimitados
- Sinal vermelho se o spender não for um contrato verificado

Resultado concreto: você entende exatamente o que está assinando, antes de assinar.

## Suporte multi-chain

A MetaMask nasceu como uma **carteira Ethereum**. Desde 2025 também suporta Solana e Bitcoin de forma nativa através das contas multichain ([MetaMask](https://metamask.io/news/multichain-accounts)). O XRP não é suportado nativamente, e as redes fora da lista integrada ainda precisam de ser adicionadas manualmente — um pequeno atrito para iniciantes.

A Lumina Wallet é **multi-chain desde o primeiro dia**: Bitcoin, Ethereum, Solana, XRP, BNB Chain, Polygon, Arbitrum, Optimism, Base, Avalanche. Uma seed phrase, um app, tudo funciona.

## Experiência do usuário: a realidade no celular

**A verdade:** o app mobile da MetaMask é criticado há anos pela UX desajeitada, pelo navegador de dApps lento e por problemas de conexão com o WalletConnect. A versão 8.0 (2026) melhorou as coisas, mas o design ainda fica visivelmente atrás das carteiras criadas primeiro para o celular.

A **Lumina Wallet** foi criada como um app mobile desde o início, com:
- Pré-visualização instantânea das transações
- Desbloqueio biométrico
- Interface limpa e moderna, projetada por designers de UX mobile
- Suporte nativo ao WalletConnect v2

## Onde a MetaMask vence

A MetaMask não vai desaparecer. Seus pontos fortes em 2026:

- ✅ **Domínio do ecossistema**: 99% dos dApps têm "Conectar com a MetaMask" como opção padrão
- ✅ **Extensão de navegador**: melhor para usuários avançados de DeFi que trabalham principalmente no desktop
- ✅ **Ecossistema Snaps**: extensibilidade por meio de plugins de terceiros
- ✅ **Código aberto**: o código é público e auditável

Se você é um usuário avançado de DeFi com disciplina de cold storage, a MetaMask atende bem. Mas o equilíbrio entre segurança e usabilidade mudou em 2026, e a Lumina é para onde os usuários comuns estão indo.

## FAQ MetaMask vs Lumina Wallet

### Posso usar as duas?

Sim — muitos usuários fazem isso. Padrão comum: MetaMask no desktop para DeFi avançado, Lumina Wallet no celular para transações diárias e para qualquer assinatura feita sob pressão.

### Minha seed phrase é compatível entre as duas?

Sim. Ambas usam o padrão BIP-39, então você pode importar a mesma seed phrase em qualquer uma das carteiras e acessar as mesmas contas.

### Qual é mais segura?

Para o **armazenamento básico de chaves**: ambas são seguras (non-custodial, criptografadas localmente). Para a **proteção ativa contra golpes**: a Lumina Wallet está significativamente à frente em 2026, graças ao filtro de tokens falsos, ao decodificador de assinaturas e ao scanner multi-chain.

### Qual é mais rápida?

Ambas são rápidas em transações básicas. Para interações com dApps, a extensão de navegador da MetaMask é mais rápida no desktop. O app mobile da Lumina Wallet é mais rápido que a MetaMask Mobile.

## Conclusão: carteiras diferentes para usuários diferentes

A MetaMask continua sendo o padrão do setor para usuários avançados de DeFi que priorizam a compatibilidade com o ecossistema e o código aberto. A Lumina Wallet é a resposta moderna para quem prioriza a **proteção contra o cenário de ameaças de 2026** — assinaturas maliciosas, tokens falsos, endereços de phishing.

Se você já ficou nervoso ao clicar em "Assinar" sem entender totalmente o que uma transação faz, a **Lumina Wallet foi feita para você**.

**Pronto para experimentar a carteira antifraude de 2026?**

[Baixar a Lumina Wallet no Google Play](https://play.google.com/store/apps/details?id=com.luminawallet.app) | [Baixar na App Store](https://apps.apple.com/us/app/lumina-wallet/id6760651380)`
  },
  "guide-wallet-non-custodial-2026": {
    title: "Carteira Cripto Não Custodial: O Guia Completo de 2026",
    meta: "O que realmente é uma carteira cripto não custodial em 2026, como ela se compara às exchanges custodiais, como escolher uma e a lista de verificação de segurança exata a seguir antes de mover um único satoshi. Escrito para utilizadores comuns.",
    body: `Uma **carteira cripto não custodial** é uma carteira em que **só você detém as chaves privadas** que controlam os seus fundos. Nenhuma empresa, nenhuma exchange, nenhum terceiro pode congelar, apreender ou perder os seus ativos em seu nome. Em 2026, depois de uma década de colapsos de exchanges (FTX, Celsius, Mt. Gox) e de bilhões em fundos de utilizadores congelados, as carteiras não custodiais passaram de uma escolha de nicho a recomendação padrão de quase todos os investigadores de segurança [1].

Este guia explica, em linguagem simples, como funcionam as carteiras não custodiais, como diferem das opções custodiais, as contrapartidas que você aceita ao escolher a autocustódia e a lista de verificação prática de 2026 para escolher uma carteira em que você possa realmente confiar.

## A Definição em 2026: Não Custodial, Explicado

Uma carteira cripto é apenas uma ferramenta para gerir **chaves privadas** — as longas sequências criptográficas que comprovam a propriedade das moedas registadas numa blockchain. Existem dois modelos de custódia:

| Modelo | Quem detém as chaves? | Quem pode mover os seus fundos? | Exemplo |
|---|---|---|---|
| **Custodial** | Uma empresa (exchange, corretora) | A empresa (e você, através dela) | Coinbase, Binance, Revolut Crypto |
| **Não custodial** | Você (no seu dispositivo) | Somente você | Lumina Wallet, MetaMask, Phantom, Ledger |

Numa configuração não custodial, as suas chaves são geradas **localmente no seu telemóvel ou dispositivo de hardware** e cifradas usando o enclave seguro do sistema operativo (iOS Secure Enclave, Android Keystore ou um chip dedicado numa carteira de hardware). A aplicação da carteira nunca as transmite para lado nenhum. Se a empresa por trás da carteira desaparecer amanhã, os seus fundos continuam acessíveis desde que você ainda tenha a sua **frase de recuperação de 12 ou 24 palavras** (BIP-39).

## Por Que 2026 É o Ano da Autocustódia

Três forças tornaram as carteiras não custodiais mainstream em 2026:

1. **As falhas de exchanges já não são eventos raros.** A Chainalysis relata que $2.8B em fundos de utilizadores continuam congelados ou perdidos desde os colapsos de exchanges de 2022–2025 [2].
2. **A MiCA e a regulação global impulsionam a prova de reservas.** Mas a prova de reservas não protege contra a insolvência operacional — apenas a autocustódia protege.
3. **As carteiras móveis alcançaram a UX.** A diferença de usabilidade entre abrir uma conta na Binance e configurar uma carteira não custodial encolheu para menos de 5 minutos.

## As Contrapartidas Reais (Versão Honesta)

Escolher uma carteira não custodial não é estritamente "melhor" — é um modelo de responsabilidade diferente. Eis a contrapartida honesta:

**O que você ganha:**
- Propriedade direta — sem risco de contraparte
- Acesso a DeFi, NFTs, swaps e staking sem KYC a cada movimento
- Resistência à censura
- Taxas mais baixas a longo prazo (sem spread custodial)

**O que você aceita:**
- **Você é o backup.** Se perder a sua frase de 24 palavras, nenhum helpdesk recupera os seus fundos.
- **Você é a equipa de segurança.** Phishing, assinaturas maliciosas, tokens falsos — a carteira ajuda, mas a decisão final é sua.
- **Sem reversão de fraude.** As transações em blockchain são definitivas.

O peso da segurança pode ser drasticamente reduzido ao escolher uma carteira com **proteção antifraude ativa** (descodificação de assinaturas, análise de endereços, filtragem de tokens falsos). Mas nunca desaparece por completo.

## Como Escolher uma Carteira Não Custodial em 2026

Nem todas as carteiras não custodiais são iguais. As alegações de marketing ("segura", "a carteira mais segura") não são critérios de avaliação. Use esta lista de verificação concreta:

### 1. Armazenamento de chaves: onde vive a seed?

- **Mínimo aceitável (carteiras de software):** chaves guardadas no iOS Secure Enclave ou no Android Keystore, cifradas com biometria ou PIN. Nunca no armazenamento simples da aplicação nem em sincronização na nuvem.
- **Segurança máxima:** carteira de hardware (Ledger, Trezor, Coldcard). As chaves nunca saem do dispositivo.

### 2. Código aberto ou transparência verificável

- Prefira carteiras com código público ou, pelo menos, uma auditoria publicada por uma empresa de renome (Trail of Bits, Halborn, Certik).
- Verifique se a empresa por trás da carteira é identificável. Uma carteira de uma entidade legal que você consiga verificar num registo público (p. ex., empresas da UE no RCS, Companies House) dificilmente desaparecerá com os seus fundos.

### 3. Funcionalidades antifraude (o diferencial de 2026)

O risco real em 2026 **não** é alguém quebrar a sua cifragem — é **você assinar uma transação maliciosa**. As melhores carteiras não custodiais evitam isto com:

- **Scanner de endereços** que verifica cada destinatário em listas conhecidas de phishing / sanções (GoPlus, Chainalysis, OFAC)
- **Descodificador de assinaturas** que traduz assinaturas ERC-20 Permit e Permit2 para linguagem simples
- **Filtro de tokens falsos** para que USDT falso recebido por airdrop não apareça ao lado do USDT verdadeiro
- **Simulação de transações** que mostra o que vai sair da sua carteira antes de você assinar

Leia: [Golpe do ERC-20 Permit Malicioso](/en/blog/malicious-erc20-permit-scam) e [Golpe de Address Poisoning](/en/blog/address-poisoning-crypto-scam).

### 4. Multi-chain, uma única frase seed

Uma carteira que suporte Bitcoin, Ethereum, Solana, XRP e as principais redes EVM a partir de uma única seed BIP-39 significa menos atrito e menos frases seed para proteger.

### 5. UX de recuperação

Teste o fluxo de recuperação antes de carregar grandes quantias. Uma boa carteira obriga você a confirmar a frase seed na primeira configuração, avisa se você tentar fazer uma captura de ecrã dela e tem documentação clara sobre como recuperar num novo dispositivo.

## A Configuração de 10 Minutos que Protege Você

1. Descarregue a carteira a partir da **página oficial da loja** (não de um anúncio de pesquisa — os burlões compram resultados patrocinados)
2. Crie uma nova carteira
3. **Escreva a frase de recuperação em papel ou num backup em metal**. Nunca a guarde numa nota na nuvem, numa captura de ecrã ou num gestor de palavras-passe com sincronização na nuvem
4. Confirme a frase na aplicação
5. Ative o desbloqueio biométrico + PIN
6. Envie primeiro um pequeno valor de teste ($5–10) a partir de uma exchange
7. Confirme que chegou
8. Só então mova quantias maiores

## Não Custodial vs. Hot/Cold: Onde Se Encaixa o Seu Telemóvel?

"Hot" e "cold" **não** são o mesmo que "custodial" e "não custodial":

- **Hot wallet** = ligada à internet (aplicação móvel, extensão de navegador)
- **Cold wallet** = offline (carteira de hardware, carteira de papel)
- Uma carteira móvel não custodial é **hot, mas não custodial**.
- Uma carteira de hardware é **cold e não custodial**.

Para o uso diário (<~$10k), uma carteira móvel não custodial segura é a recomendação mainstream de 2026. Para poupanças superiores a isso, adicione uma camada de carteira de hardware.

## FAQ sobre Carteira Cripto Não Custodial

### Uma carteira não custodial é legal?
Sim. Em todas as principais jurisdições (EUA, UE sob a MiCA, Reino Unido, Canadá, Japão, Singapura), deter as suas próprias chaves é legal. As obrigações de reporte aplicam-se aos **movimentos de entrada/saída de moeda fiduciária**, não à autocustódia em si. Verifique as regras fiscais locais.

### Posso perder os meus fundos com uma carteira não custodial?
Sim, de três formas: (1) perder a frase de recuperação, (2) assinar uma transação maliciosa, (3) enviar para o endereço errado. As três são mitigáveis com backups adequados e uma carteira com funcionalidades antifraude ativas.

### A Coinbase Wallet é não custodial?
A Coinbase Wallet (a aplicação independente) é não custodial. A conta da **exchange** Coinbase é custodial. São dois produtos diferentes com o mesmo nome de marca.

### Qual é a melhor carteira não custodial para iniciantes em 2026?
Veja a nossa comparação dedicada: [Melhor carteira cripto não custodial 2026](/en/blog/best-non-custodial-crypto-wallet-2026). Versão curta: escolha uma carteira mobile-first com descodificação de assinaturas e análise de endereços integradas.

## Conclusão

Uma carteira cripto não custodial não é um luxo — em 2026 é a base para qualquer pessoa que detenha mais do que algumas centenas de dólares em cripto. A curva de aprendizagem é real, mas curta. As decisões principais são: escolher uma carteira com proteção antifraude moderna, fazer backup da frase seed em papel, começar com uma pequena transferência de teste e tratar a sua frase de recuperação como a chave mestra que ela é.

**Pronto para a autocustódia com proteção antifraude integrada?** [Descarregue a Lumina Wallet no Google Play](https://play.google.com/store/apps/details?id=com.luminawallet.app) | [App Store](https://apps.apple.com/us/app/lumina-wallet/id6760651380).

---

### Referências

[1] Electronic Frontier Foundation. (2026). *Self-custody as a digital-rights baseline*. [https://www.eff.org/](https://www.eff.org/)

[2] Chainalysis. (2026). *The State of Centralized Exchange Insolvencies*. [https://www.chainalysis.com/](https://www.chainalysis.com/)`
  },
  "faux-wallets-crypto-2026-7-clones": {
    title: "Carteiras Cripto Falsas em 2026: 7 Novos Ataques de Clonagem que Você Precisa Conhecer",
    meta: "Um guia de campo de 2026 sobre os 7 ataques mais ativos de carteiras cripto falsas: clones em lojas de apps com anúncios patrocinados, domínios parecidos, carteiras de \"suporte\" no Telegram, APKs modificados, vídeos deepfake de fundadores, falsas atualizações do MetaMask e golpes de restauração de seed. Com passos de prevenção.",
    body: `Em 2026, os ataques com carteiras cripto falsas cresceram mais rápido do que qualquer outra categoria de roubo de criptoativos. A Chainalysis estima que **US$ 340 milhões foram perdidos em esquemas de carteiras falsas somente no primeiro semestre de 2026** [1], impulsionados pela profissionalização das operações de golpe: anúncios pagos em buscadores, infraestrutura de clonagem dinâmica, vídeos de fundadores gerados por deepfake e APKs Android modificados com cargas maliciosas que roubam a seed.

Este artigo é um guia de campo. Para cada um dos 7 padrões de ataque mais ativos de 2026, você encontra: como funciona, o truque técnico e o único passo que o neutraliza.

## 1. Clones em Lojas de Apps com Anúncios Patrocinados

**Como funciona.** Os atacantes publicam no Google Play ou na App Store uma cópia quase idêntica de uma carteira legítima — mesmo ícone, nome parecido ("Lumina Wallet Pro", "MetaMask Lite", "Phantom Wallet Vault"). Em seguida, compram posicionamentos patrocinados no Google Search para o nome da marca da carteira real. O resultado patrocinado coloca a falsa acima da verdadeira.

**O truque técnico.** O app falso executa uma interface simplificada que se parece com a da carteira real. Ao criar ou importar a seed, ele envia silenciosamente a mnemônica para o servidor do atacante. O usuário vê a tela padrão de "carteira pronta" enquanto os fundos são drenados em segundo plano.

**Como neutralizar.** Nunca instale uma carteira a partir de um anúncio de busca. Vá diretamente ao domínio oficial do fornecedor (digite-o manualmente, não pela busca), encontre ali o selo da loja e toque nele. O Google recomenda oficialmente esse fluxo para apps de cripto [2]. Veja também: [Clones de carteiras cripto falsas](/en/blog/fake-crypto-wallet-clones).

## 2. Domínios Parecidos (Punycode e Typosquats)

**Como funciona.** Os atacantes registram domínios visualmente indistinguíveis do original — "luminawallet.com" vs. "lumınawallet.org" (i sem ponto), "metamask.io" vs. "metamαsk.io" (alfa grego), "trustwallet.com" vs. "trust-wallet.com".

**O truque técnico.** As barras de endereço dos navegadores exibem caracteres Unicode idênticos às letras latinas (ataque homógrafo IDN). Muitos usuários de carteiras não percebem.

**Como neutralizar.** Use sempre um favorito para a página web da carteira. Nunca digite o domínio sob pressão de tempo. Gerenciadores de senhas que fazem correspondência exata de domínio se recusarão a preencher automaticamente em um domínio parecido — isso é um sinal para parar.

## 3. Golpes de "Suporte no Telegram" para Carteiras

**Como funciona.** Você publica uma reclamação em um grupo cripto legítimo ("Minha transação está travada"). Em minutos, uma conta chamada "Lumina Wallet Support" ou "MetaMask Help" envia uma DM oferecendo ajuda. Ela manda um link para um "painel de suporte" e pede que você conecte sua carteira ou cole sua seed para "verificar a propriedade".

**O truque técnico.** Nenhuma empresa de carteira real faz suporte no Telegram desse jeito. Os golpistas usam bots de scraping que detectam menções a marcas de carteiras e enviam DMs em segundos.

**Como neutralizar.** Nenhum suporte legítimo de carteira jamais pedirá sua seed phrase, sua chave privada ou que você se conecte a um dApp desconhecido. Todos os canais de suporte reais são acessíveis de dentro do app oficial — e não o contrário. Veja [Golpes de recuperação cripto com falso suporte](/en/blog/fake-support-crypto-recovery-scams).

## 4. APKs Android Modificados (Ataques de Side-Loading)

**Como funciona.** No Android, o usuário pesquisa "download MetaMask APK" e instala uma versão modificada de um site de terceiros. O app modificado tem a mesma aparência e o mesmo comportamento do original, mas contém uma carga maliciosa que intercepta a geração da seed.

**O truque técnico.** O atacante pega o APK open source real, injeta código malicioso no fluxo de geração de chaves (de modo que a entropia fique parcialmente sob controle do atacante), o assina novamente e o distribui em sites espelho de APK.

**Como neutralizar.** No Android, instale carteiras somente pela Google Play Store. Mantenha desativada, por padrão, a opção "Instalar de fontes desconhecidas". Usuários de iPhone estão naturalmente protegidos pelo sandbox da App Store.

## 5. Vídeos Deepfake de Fundadores (Golpes de Sorteio)

**Como funciona.** Anúncios no YouTube e no TikTok mostram "Vitalik Buterin", "Changpeng Zhao" ou o verdadeiro fundador de uma carteira anunciando um "sorteio de atualização da carteira". Envie 1 ETH para um QR code → receba 2 ETH de volta. O vídeo é um deepfake.

**O truque técnico.** Modelos open source de 2026 (SadTalker-v4, HeyGen-OSS, clones do VASA-1) conseguem gerar vídeos convincentes com sincronia labial a partir de uma única foto e de uma amostra de voz clonada. A equipe de análise da Lumina encontrou 23 vídeos deepfake diferentes imitando fundadores de grandes carteiras somente no 3º trimestre de 2026.

**Como neutralizar.** Nenhuma carteira legítima faz promoções do tipo "envie X, receba 2X". Nunca. Esse é um sinal de golpe 100% confiável. Além disso: eventos ao vivo com fundadores são mais fáceis de verificar como reais do que vídeos pré-gravados.

## 6. Pop-ups Falsos de "Atualização da Carteira" no Navegador

**Como funciona.** Você visita qualquer site de cripto. Um pop-up ou banner injetado diz "Sua MetaMask está desatualizada. Clique para atualizar". O link leva a uma página que instala uma extensão de navegador maliciosa ou uma carteira modificada.

**O truque técnico.** Muitos desses pop-ups vêm de redes de anúncios de terceiros comprometidas em sites de cripto legítimos. A carteira real nunca exibe avisos de atualização dessa forma — as extensões de navegador são atualizadas pela loja do Chrome / Firefox e os apps móveis pela loja do sistema operacional.

**Como neutralizar.** Ignore todos os avisos de "atualize sua carteira" dentro de páginas. Verifique atualizações somente na loja de extensões do navegador ou na loja de apps do seu celular. Se um aviso insistir, feche a aba.

## 7. Golpes de "Migração" com Restauração de Seed

**Como funciona.** Você recebe um e-mail, SMS ou DM no app: "Sua carteira está sendo migrada para uma nova versão segura. Insira novamente sua seed phrase nesta página para migrar seus ativos". A mensagem imita a identidade visual da sua carteira real.

**O truque técnico.** O golpe explora o fato de que carteiras reais, de vez em quando, mudam de marca ou migram de fato. Os atacantes imitam esses eventos legítimos (às vezes dentro de 48 horas após o anúncio de uma empresa real) para reduzir a desconfiança do usuário.

**Como neutralizar.** Nenhuma migração de carteira exige reinserir sua seed em uma página web ou formulário. Migrações reais acontecem dentro do app ou são uma simples atualização pela loja. Em caso de dúvida, abra diretamente o app oficial e procure um comunicado dentro dele.

## Referência Rápida: Sinais de Alerta de Carteiras Falsas em 2026

| Sinal de alerta | O que está acontecendo |
|---|---|
| "Insira sua seed phrase para verificar / restaurar / migrar" | Phishing. Sempre. |
| Resultado patrocinado pelo Google para o nome de uma marca de carteira | Provável clone. Role a página para baixo. |
| Download de APK de um site que não seja a Play Store | Malware modificado. |
| DM de "suporte da carteira" após uma reclamação pública | Bot de scraping. Denuncie e bloqueie. |
| "Envie X, receba 2X" de um fundador | Deepfake / sorteio falso. |
| Pop-up na página pedindo que você atualize sua carteira | Anúncio injetado. Ignore. |
| O gerenciador de senhas se recusa a preencher o domínio da "carteira" | Domínio parecido. Pare. |

## Como a Lumina Wallet Protege Contra Fluxos de Carteiras Falsas

A Lumina Wallet integra as defesas práticas deste guia:

- **Verificação da fonte de instalação** — a tela do primeiro uso mostra a integridade do pacote do app, para que um APK modificado seja detectável
- **Decodificação de assinaturas** — transações maliciosas vindas de dApps clonados são sinalizadas em linguagem clara antes da assinatura
- **Scanner de endereços** — os endereços de destino são checados em bancos de dados conhecidos de phishing / sanções
- **Alerta de visibilidade da seed phrase** — o app detecta tentativas de captura de tela na tela da seed e as bloqueia no Android

Veja o detalhamento completo: [A abordagem de segurança da Lumina](/en/security).

## Perguntas Frequentes sobre Carteiras Falsas

### Uma carteira falsa pode roubar meus fundos apenas por estar instalada?
Não imediatamente. A maioria das carteiras falsas rouba fundos quando você (a) importa uma seed existente nelas, (b) cria uma nova seed dentro delas ou (c) assina uma transação que elas elaboram. Instalar e nunca usar é relativamente seguro — mas desinstale mesmo assim.

### Como verifico se a minha carteira atual é a verdadeira?
No Android, verifique a fonte do instalador: Configurações → Apps → [Carteira] → Informações do app → Instalador. Deve constar "Google Play Store" (com.android.vending). No iOS, apps instalados pela App Store real não podem ser modificados dessa forma; o risco é menor. Compare também o nome do publicador com o domínio do fornecedor real.

### Acho que inseri minha seed em um site falso. E agora?
Ação imediata: abra sua carteira legítima e transfira todos os tokens para uma carteira nova que você criar (com nova seed). A seed antiga está comprometida para sempre. Veja [Golpes de recuperação cripto com falso suporte](/en/blog/fake-support-crypto-recovery-scams).

### As carteiras de hardware são imunes a esses ataques?
Em grande parte, sim. As carteiras de hardware protegem a chave, então até um app complementar falso no seu celular não consegue exfiltrá-la. No entanto, uma transação maliciosa criada por um dApp falso ainda será exibida na carteira de hardware, e você ainda precisa não aprová-la. Leia a tela do dispositivo.

## Conclusão

As carteiras cripto falsas de 2026 não são operações amadoras — são profissionais, bem financiadas e evoluem rápido. A defesa é simples, mas confiável: instale apenas pelas lojas oficiais, nunca digite sua seed fora do app que a gerou, use uma carteira com decodificação de assinaturas e salve os domínios verdadeiros nos favoritos. Quatro hábitos, nenhuma exceção.

**Instale a Lumina Wallet — somente pelas lojas oficiais:** [Google Play](https://play.google.com/store/apps/details?id=com.luminawallet.app) | [App Store](https://apps.apple.com/us/app/lumina-wallet/id6760651380).

---

### Referências

[1] Chainalysis. (2026). *Mid-Year Crypto Crime Report 2026 — Fake Wallet Threats*. [https://www.chainalysis.com/](https://www.chainalysis.com/)

[2] Google Safety. (2026). *Crypto Wallet App Safety Guidelines*. [https://safety.google/](https://safety.google/)`
  },
  "checklist-securite-wallet-crypto-2026": {
    title: "Checklist de Segurança para Carteiras Cripto 2026",
    meta: "Um checklist de segurança prático e sem exageros para qualquer utilizador de carteiras cripto em 2026. Quinze itens agrupados em configuração, uso diário, armazenamento a longo prazo e recuperação — escrito para utilizadores comuns, não para auditores.",
    body: `A maioria dos artigos com "dicas de segurança" para carteiras cripto é demasiado técnica (modelos de ameaça para auditores) ou demasiado vaga ("tenha cuidado!"). Esta é uma lista diferente: **quinze ações concretas e testáveis**, agrupadas por momento da sua jornada cripto, com o ataque específico que cada uma bloqueia. Se só ler uma lista este ano sobre segurança de carteiras, esta é a que vale a pena imprimir.

## Secção 1 — Configuração (Primeiros 15 Minutos)

### 1. Instale a partir da loja oficial, nunca a partir de um anúncio de pesquisa
**Bloqueia:** Clones de listagens patrocinadas nas lojas e APKs modificados.
Escreva à mão o domínio real do fabricante, encontre lá o selo da loja e toque nele. Nunca instale a partir de um resultado patrocinado do Google.

### 2. Verifique o nome do editor na listagem
**Bloqueia:** Aplicações semelhantes com ícones parecidos.
No Google Play e na App Store, o nome do editor aparece abaixo do título da aplicação. Tem de corresponder à empresa que espera (por exemplo, "HEDONISME SARL" para a Lumina Wallet). Se disser "Lumina Studios LLC", é um clone.

### 3. Crie uma nova carteira — não importe logo uma antiga para um telemóvel novo
**Bloqueia:** Situações de seed comprometida.
Se está a configurar uma carteira pela primeira vez, gere uma seed nova. Só importe uma seed existente se precisar especificamente de aceder a esses fundos.

### 4. Escreva a frase seed em papel ou num backup metálico
**Bloqueia:** Fugas na cloud e malware de capturas de ecrã.
Nunca guarde a seed numa nota na cloud (Google Keep, iCloud Notes, Dropbox), como captura de ecrã, num gestor de palavras-passe sincronizado na cloud ou nos rascunhos do seu email. Apenas papel ou uma placa de aço (Billfodl, Cryptosteel).

### 5. Confirme a seed na aplicação e teste a recuperação antes de carregar fundos
**Bloqueia:** Erros de digitação na configuração que só são descobertos tarde demais.
A maioria das carteiras pede-lhe que confirme a seed selecionando novamente as palavras. Faça-o com cuidado. Depois — antes de transferir dinheiro real — apague e recupere a carteira uma vez usando o seu backup escrito. Se recuperar corretamente, o seu backup funciona.

## Secção 2 — Uso Diário

### 6. Bloqueie a aplicação com biometria + PIN
**Bloqueia:** Roubo do telemóvel com o dispositivo desbloqueado.
Ambas as camadas contam: biometria para a conveniência diária, PIN como alternativa caso o ladrão consiga o ângulo do seu rosto.

### 7. Use uma carteira com descodificador de assinaturas
**Bloqueia:** Assinaturas maliciosas ERC-20 Permit e Permit2.
Uma boa carteira traduz as assinaturas para linguagem simples ("Autoriza [contrato desconhecido] a gastar USDT ILIMITADO") antes de assinar. Se a sua mostra hex em bruto, mude de carteira. Veja o nosso artigo aprofundado sobre [burlas de ERC-20 Permit maliciosas](/en/blog/malicious-erc20-permit-scam).

### 8. Ative a filtragem de tokens falsos (ou use uma carteira que o faça por defeito)
**Bloqueia:** Burlas de airdrop em que um USDT falso aparece junto ao verdadeiro.
A Lumina Wallet filtra tokens pelo endereço de contrato oficial. Se a sua carteira mostra todos os airdrops, trate cada token inesperado como suspeito até ser provado real num explorador de blocos.

### 9. Verifique o endereço do destinatário com um scanner on-chain
**Bloqueia:** Address poisoning e endereços sancionados.
As carteiras modernas verificam cada endereço de saída em tempo real contra listas da GoPlus, Chainalysis e OFAC. Se um endereço for sinalizado — pare. Leia: [burla de address poisoning](/en/blog/address-poisoning-crypto-scam).

### 10. Teste um novo destinatário primeiro com um valor pequeno
**Bloqueia:** Erros de digitação, address poisoning, endereços copiados e colados que foram substituídos por phishing.
Antes de qualquer transferência superior a ~$500, envie primeiro ~$5–10. Confirme que chega à carteira correta. Depois envie o restante.

## Secção 3 — Transações e dApps

### 11. Nunca assine uma assinatura que não compreende
**Bloqueia:** Todos os esvaziamentos baseados em assinaturas (Permit, Permit2, SeaportOrder, aprovações ERC-721).
Se uma carteira ou dApp lhe mostrar um pedido de assinatura com campos pouco claros, não assine. Em 2026, **não existe nenhuma razão legítima** para assinar uma assinatura opaca.

### 12. Trate "Aprovar ilimitado" como um sinal de alerta
**Bloqueia:** Aprovações de gasto infinito que ficam ativas para sempre.
Quando uma dApp pede aprovação de um token ERC-20, prefira "valor personalizado" (apenas o montante desta transação) em vez de "ilimitado". Reveja e revogue periodicamente aprovações ilimitadas antigas através do Revoke.cash ou do gestor de aprovações da sua carteira.

### 13. Desligue as dApps que já não usa
**Bloqueia:** Permissões dormentes a serem exploradas mais tarde.
Cada carteira tem uma vista de "sites ligados" ou "aplicações ligadas". Se não reconhecer uma ligação, desligue-a. Fazer isto mensalmente demora 30 segundos.

## Secção 4 — Armazenamento a Longo Prazo e Recuperação

### 14. Use uma carteira de hardware para valores acima da sua "linha de conforto"
**Bloqueia:** Malware no dispositivo a exfiltrar chaves.
Cada pessoa tem um limiar diferente a partir do qual "ficaria muito chateado se isto fosse roubado". Para a maioria dos utilizadores em 2026, essa linha situa-se entre $5k e $20k. Acima dela, uma carteira de hardware compensa os ~$70–$150.

### 15. Divida os seus ativos por pelo menos duas carteiras
**Bloqueia:** O comprometimento de uma única carteira levar tudo.
Separe a "carteira de gastos" (móvel, conveniente, montantes menores) da "carteira de poupança" (carteira de hardware ou carteira de software com backups robustos, fria). As transferências entre as duas são raras e deliberadas.

## Resumo para Imprimir

| # | Ação | Bloqueia |
|---|---|---|
| 1 | Instalar a partir da loja oficial | Clones de listagens nas lojas |
| 2 | Verificar o nome do editor | Aplicações semelhantes |
| 3 | Criar uma nova carteira | Seeds comprometidas |
| 4 | Backup em papel/metal | Fugas na cloud |
| 5 | Testar a recuperação antes de carregar fundos | Erros de digitação na configuração |
| 6 | Biometria + PIN | Roubo do telemóvel |
| 7 | Descodificador de assinaturas | Esvaziamentos por Permit/Permit2 |
| 8 | Filtro de tokens falsos | Burlas de airdrop |
| 9 | Scanner de endereços on-chain | Address poisoning |
| 10 | Pequena transferência de teste | Erros de digitação, poisoning |
| 11 | Não assinar assinaturas opacas | Todas as burlas de assinatura |
| 12 | Evitar aprovações ilimitadas | Esvaziamentos de gasto infinito |
| 13 | Desligar dApps não utilizadas | Explorações dormentes |
| 14 | Carteira de hardware para poupança | Malware no dispositivo |
| 15 | Separar gastos de poupança | Comprometimento de carteira única |

## Como a Lumina Wallet Cobre Este Checklist

Os itens 6–11 e 13 estão integrados na Lumina Wallet por defeito — sem necessidade de configuração. Os itens 1–5, 12, 14 e 15 dependem dos seus hábitos pessoais e nenhuma carteira os pode impor por si.

Veja a descrição completa das funcionalidades: [funcionalidades da Lumina Wallet](/en/features) e [abordagem de segurança](/en/security).

## FAQ sobre Segurança de Carteiras Cripto

### Com que frequência devo rever este checklist?
Uma releitura completa a cada 6 meses é suficiente. Os itens rápidos (3, 9, 13) vale a pena fazê-los antes de cada nova transação superior a $500.

### Um gestor de palavras-passe é seguro para guardar uma frase seed?
Apenas se o gestor de palavras-passe não tiver qualquer sincronização na cloud (apenas cofre local) e o dispositivo estiver encriptado. Para a maioria dos utilizadores, papel ou metal é mais simples e seguro. Gestores sincronizados na cloud = cloud = fora de questão para o armazenamento de seeds.

### E se o meu telemóvel for roubado com a carteira desbloqueada?
Se tinha os itens 6 (biometria + PIN) e 15 (ativos divididos) implementados, a exposição da sua carteira de gastos é limitada e a sua poupança fica intacta. Mesmo assim, revogue imediatamente as ligações a dApps a partir de outro dispositivo e, se tiver a seed, transfira os fundos para uma nova carteira.

### A atividade cripto no navegador continua segura em 2026?
Pode ser, com uma carteira em extensão de boa reputação, um perfil de navegador dedicado e uma revisão rigorosa das assinaturas. Mas a superfície de ataque é maior do que no telemóvel. Para a maioria dos utilizadores comuns, as carteiras móveis com proteção integrada são uma melhor escolha por defeito em 2026.

## Conclusão

A segurança de carteiras cripto em 2026 é 90 % hábitos e 10 % tecnologia. A parte tecnológica importa — uma carteira com descodificação de assinaturas, análise de endereços e filtragem de tokens falsos bloqueia ataques que nenhuma disciplina consegue travar sozinha. Mas os quinze hábitos acima são o que separa um utilizador que dorme descansado de um que aprende sobre higiene de frases seed da pior maneira.

**Experimente a carteira com os itens 6–11 e 13 integrados:** [Lumina Wallet no Google Play](https://play.google.com/store/apps/details?id=com.luminawallet.app) | [App Store](https://apps.apple.com/us/app/lumina-wallet/id6760651380).

---

### Referências

[1] NIST. (2026). *Digital Identity Guidelines — Cryptographic Key Management for Consumer Wallets*. [https://www.nist.gov/](https://www.nist.gov/)

[2] GoPlus Labs. (2026). *2026 Crypto Threat Intelligence Report*. [https://gopluslabs.io/](https://gopluslabs.io/)`
  },
  "verifier-app-wallet-crypto-authentique": {
    title: "Como verificar se um aplicativo de carteira cripto é verdadeiro: checklist de verificação 2026",
    meta: "Antes de instalar um aplicativo de carteira cripto, faça estas verificações: link oficial, publicador, ID do app, permissões e comportamento da frase de recuperação. Um checklist de 5 minutos para identificar apps de carteira falsos.",
    body: `Aplicativos de carteira falsos são uma das formas mais simples de perder cripto: você instala o que parece uma carteira normal, cria ou importa uma frase de recuperação, e o app envia essa frase discretamente ao golpista. Sem invasão, sem exploit: apenas o app errado.

A boa notícia: a maioria dos apps de carteira falsos não passa em algumas verificações básicas. Este guia apresenta uma **rotina de verificação que você pode executar em cerca de cinco minutos**, antes de instalar qualquer carteira, inclusive a Lumina Wallet.

> Já conhece os principais sinais de alerta? Nosso guia [Carteiras cripto falsas: como identificar os clones](/pt/blog/faux-wallets-crypto-clones) explica como os apps clonados funcionam. Este artigo é o checklist prático.

## Passo 1: comece pelo site oficial, não por um anúncio de busca

A porta de entrada mais comum para uma carteira falsa é um **resultado patrocinado** ou um anúncio que aparece acima do resultado real. Golpistas compram anúncios com nomes de carteiras porque as pessoas pesquisam por eles.

- Digite você mesmo o endereço do site oficial ou use um favorito que você criou anteriormente.
- Use os links de download publicados nesse site, não o primeiro link de um buscador ou de uma rede social.
- Cuidado com domínios parecidos: uma letra a mais, uma extensão diferente (.app, .io, .net) ou caracteres que parecem idênticos (por exemplo, "rn" em vez de "m").

Para a Lumina Wallet, os links oficiais de download estão listados na [página de download](/pt/download).

## Passo 2: verifique o publicador na página da loja

Na App Store ou no Google Play, abra a página do app e olhe o **nome do desenvolvedor / publicador**, e não apenas o nome e o logotipo do app, que são fáceis de copiar.

- O publicador deve corresponder à empresa ou ao projeto por trás do site oficial.
- Toque no nome do publicador: uma equipe de carteira legítima normalmente tem um histórico consistente, não uma lista de apps sem relação publicados na semana passada.
- Leia as avaliações mais recentes. Várias avaliações mencionando "os fundos desapareceram após a importação" são um forte sinal de alerta. Muitas avaliações genéricas de cinco estrelas publicadas em poucos dias são outro.

## Passo 3: compare o identificador do app

Todo app tem um identificador único que um clone não pode reutilizar nas lojas oficiais:

- **Android:** o nome do pacote na URL do Google Play (depois de \`id=\`).
- **iOS:** o ID numérico na URL da App Store (depois de \`/id\`).

Compare-o com o identificador do link do site oficial. Se o site oficial aponta para um ID e o app que você encontrou tem outro, não é o mesmo app.

## Passo 4: nunca instale uma carteira a partir de um arquivo enviado a você

Uma carteira instalada a partir de um arquivo APK compartilhado no Telegram, Discord, WhatsApp ou por e-mail ignora completamente a revisão da loja. Versões "modificadas", "pro" ou "desbloqueadas" de uma carteira são uma armadilha clássica.

- Instale carteiras somente pelas lojas oficiais, usando os links do site oficial.
- Se alguém insistir que você instale uma versão especial "para corrigir sua conta", trate como golpe. Nosso artigo sobre [golpes de falso suporte e de recuperação](/pt/blog/faux-support-arnaques-recuperation-crypto) aborda esse padrão.

## Passo 5: observe as permissões (Android)

Um app de carteira precisa de muito pouco: acesso à rede e, às vezes, à câmera para ler códigos QR. Desconfie se uma carteira pedir:

- **Serviços de acessibilidade** (podem ler e controlar o que está na sua tela),
- **Exibição sobre outros apps** (pode mostrar telas falsas por cima das reais),
- **Acesso a SMS** ou direitos de **administrador do dispositivo**.

Nenhuma dessas permissões é necessária para enviar ou receber cripto.

## Passo 6: observe como o app trata a sua frase de recuperação

Esta é a verificação mais importante, porque a frase de recuperação é o que o golpista quer.

- Uma carteira não custodial legítima **gera** a sua frase de recuperação no seu dispositivo quando você cria uma nova carteira. Ela pede que você a anote offline.
- Ela nunca pede que você envie a frase ao "suporte", que a "valide" ou "sincronize" em um site, nem que a digite para "desbloquear" uma recompensa.
- Se um app pedir que você importe imediatamente uma frase existente, com pressão ("sua carteira será suspensa"), pare.

Em caso de dúvida, **crie uma nova carteira** no app em vez de importar a sua frase de recuperação principal, e teste-a primeiro.

## Passo 7: teste com um valor pequeno

Antes de mover fundos significativos:

1. Envie um valor pequeno para a nova carteira.
2. Verifique a transação em um explorador de blocos público.
3. Envie parte do valor de volta para um endereço que você controla.

Se algo se comportar de forma estranha (saldo não exibido, endereço mudando inesperadamente, pedido de uma "taxa" para desbloquear seus fundos), pare de usar o app.

## O checklist de 5 minutos

| Verificação | Como é um app legítimo |
| --- | --- |
| Origem | Link obtido no site oficial, não em um anúncio ou mensagem |
| Publicador | Mesma entidade do site oficial, histórico consistente |
| Identificador do app | Corresponde ao ID vinculado no site oficial |
| Instalação | Somente loja oficial, nunca um arquivo APK enviado a você |
| Permissões | Sem acessibilidade, sobreposição, SMS ou direitos de administrador |
| Frase de recuperação | Gerada localmente, nunca solicitada pelo "suporte" ou por um site |
| Primeiro uso | Pequena transação de teste antes de mover valores maiores |

## O que fazer se você instalou uma carteira falsa

Se você digitou a sua frase de recuperação em um app do qual agora desconfia, presuma que a frase foi comprometida:

1. **Aja rapidamente.** Instale uma carteira legítima a partir da fonte oficial em um dispositivo de confiança e crie uma **nova** carteira com uma nova frase de recuperação.
2. **Mova seus fundos** dos endereços antigos para a nova carteira.
3. **Revise as aprovações de tokens** vinculadas aos endereços antigos com uma ferramenta de verificação de aprovações de boa reputação e revogue as que você não reconhece.
4. **Desinstale o app falso** e denuncie-o à loja.
5. **Ignore qualquer pessoa que se ofereça para recuperar seus fundos** mediante pagamento: essas ofertas quase sempre são um segundo golpe.

## FAQ

**Um app de carteira falso pode estar nas lojas oficiais?**
Sim, isso acontece: alguns clones passam na revisão por um tempo antes de serem removidos. Por isso, verificar o publicador e o identificador do app é importante, e não apenas a loja.

**Uma carteira com muitos downloads é sempre legítima?**
Não. O número de downloads e as avaliações podem ser inflados. Use-os como um sinal entre outros, nunca como prova.

**A Lumina Wallet alguma vez pede a minha frase de recuperação?**
Não. A Lumina Wallet é não custodial: sua frase de recuperação permanece no seu dispositivo e ninguém da Lumina vai pedi-la.

**Como mantenho minha carteira segura depois de instalá-la?**
Siga nosso [Checklist de segurança de carteira cripto 2026](/pt/blog/checklist-securite-wallet-crypto-2026) para configuração, uso diário e recuperação.

## Conclusão

Um app de carteira falso só funciona se você pular as verificações básicas. Pegue o link no site oficial, compare o publicador e o identificador do app, recuse arquivos enviados por desconhecidos e nunca entregue sua frase de recuperação a ninguém. Cinco minutos de verificação custam muito menos do que uma carteira perdida.

Para instalar a Lumina Wallet, use os links da [página de download](/pt/download) oficial.`
  },
  "crypto-volees-que-faire": {
    title: "Recuperação de Golpes na Ethereum: O Que Você Pode Realmente Fazer Depois de Ter Suas Criptomoedas Roubadas (2026)",
    meta: "Criptomoedas roubadas em um golpe? O que você pode fazer de forma realista nas primeiras 24 horas: proteger sua carteira, guardar provas, rastrear os fundos, denunciar à polícia, às exchanges e aos emissores de stablecoins, e evitar golpes de recuperação.",
    body: `Se seus ETH ou tokens foram roubados por meio de um golpe, de um site de phishing ou de uma assinatura maliciosa, as primeiras horas são decisivas. Este guia explica **o que você pode fazer de forma realista**, em que ordem, e como evitar perder ainda mais dinheiro para os "especialistas em recuperação" que abordam as vítimas logo após um roubo.

Um ponto de honestidade antes de tudo: transações em blockchain não podem ser canceladas. Ninguém — nem sua carteira, nem a Ethereum, nem a Lumina Wallet — pode apertar um botão e revertê-las. A recuperação às vezes é possível, mas somente por caminhos específicos: uma exchange ou um emissor de stablecoin congelando os fundos, geralmente após uma denúncia oficial.

## Passo 1: Pare o Prejuízo (Primeiros Minutos)

Antes de tentar recuperar qualquer coisa, proteja o que sobrou.

- **Não envie mais dinheiro** a ninguém que prometa "desbloquear", "liberar" ou "recuperar" seus fundos.
- Se sua **frase de recuperação** pode ter sido comprometida (você a digitou em um site, em um aplicativo de que agora desconfia, ou a entregou ao "suporte"), crie uma **nova carteira com uma nova frase de recuperação** em um dispositivo de sua confiança e transfira para ela os fundos restantes.
- Se o roubo veio de uma **aprovação ou assinatura maliciosa**, revise as aprovações de tokens do seu endereço com um verificador de aprovações confiável (por exemplo, a ferramenta de aprovação de tokens da Etherscan) e revogue as que você não reconhece. Um drainer pode continuar usando uma aprovação até que ela seja revogada.
- Desconecte a carteira do site envolvido e desinstale qualquer aplicativo ou extensão de navegador suspeito.

## Passo 2: Reúna as Provas

Todas as etapas seguintes dependem de informações precisas. Anote ou salve:

- os **hashes das transações** (TxID) das transferências roubadas;
- seu endereço e os **endereços de destino**;
- data, hora e valores;
- a URL do site, o nome do aplicativo, os perfis de redes sociais e os nomes de usuário envolvidos;
- capturas de tela de conversas, anúncios, e-mails e das páginas que você usou.

Guarde as mensagens originais: não apague a conversa com o golpista.

## Passo 3: Siga os Fundos em um Explorador de Blocos

Cole o hash da transação em um explorador de blocos como a Etherscan. Veja para onde os fundos foram em seguida:

- Se foram para um endereço identificado como de uma **exchange centralizada** (um endereço de depósito de uma plataforma conhecida), essa é a pista mais útil: exchanges regulamentadas podem congelar uma conta, geralmente a pedido da polícia.
- Se os ativos roubados são **USDT ou USDC**, seus emissores têm a capacidade técnica de congelar tokens mantidos em um endereço, normalmente em cooperação com as autoridades. A rapidez é essencial, pois os golpistas costumam trocá-los depressa.
- Se os fundos passaram por um **mixer** ou por muitos endereços intermediários, a recuperação fica muito mais difícil. Denuncie mesmo assim.

Você não precisa ser especialista: os hashes das transações e os endereços de destino são o que investigadores e exchanges vão pedir.

## Passo 4: Faça a Denúncia Oficial

Uma denúncia oficial é o que torna um congelamento possível. Registre-a o mais rápido que puder:

- **A polícia** do seu país, ou o serviço de denúncia de crimes cibernéticos (nos Estados Unidos, o IC3 do FBI, em ic3.gov). Peça um número de boletim ou de referência.
- **A exchange** que recebeu os fundos: entre em contato com o suporte oficial por meio do site ou do aplicativo (nunca por um link que lhe enviaram), com os hashes das transações e o número de referência da polícia.
- **O emissor da stablecoin**, se USDT ou USDC foram roubados, por meio do site oficial.
- **Bancos de dados públicos de golpes**, como a Chainabuse, para alertar outros usuários sobre os endereços envolvidos.
- **A plataforma em que você conheceu o golpista** (rede social, loja de aplicativos, rede de anúncios), para que a conta, o aplicativo ou o anúncio sejam removidos.

## Passo 5: Cuidado com os "Especialistas em Recuperação"

Logo após um roubo, as vítimas costumam ser contatadas por pessoas que afirmam poder "hackear de volta", "rastrear e recuperar" ou "desbloquear" criptomoedas roubadas, mediante o pagamento de uma taxa adiantada. Na prática, essas ofertas são um conhecido segundo golpe.

Sinais de alerta:

- eles entram em contato primeiro, muitas vezes em comentários ou mensagens privadas depois que você falou sobre o roubo;
- garantem resultados ou citam uma taxa de sucesso precisa;
- pedem uma taxa adiantada, um "imposto", "gas" ou uma "taxa de liberação";
- pedem sua frase de recuperação ou acesso remoto ao seu dispositivo.

As autoridades de verdade nunca cobram para recuperar seus fundos. Se quiser ajuda profissional, escolha um advogado habilitado ou uma empresa consolidada de investigação em blockchain cuja identidade você possa verificar, e nunca pague a um desconhecido que entrou em contato com você. Nosso artigo sobre [golpes de recuperação de cripto e falso suporte](/pt/blog/faux-support-arnaques-recuperation-crypto) descreve esses esquemas em detalhes.

## As Primeiras 24 Horas em Resumo

| Quando | O que fazer |
| --- | --- |
| Primeiros minutos | Pare de pagar qualquer pessoa, proteja os fundos restantes, revogue aprovações suspeitas |
| Primeira hora | Salve hashes de transações, endereços, URLs e capturas de tela |
| Primeiras horas | Rastreie os fundos em um explorador de blocos, identifique exchanges ou stablecoins |
| No mesmo dia | Registre um boletim de ocorrência, contate a exchange e o emissor por canais oficiais |
| Dias seguintes | Denuncie os endereços publicamente, ignore ofertas de "recuperação" não solicitadas |

## Quais São as Chances de Recuperar Suas Criptomoedas?

Sendo honesto, são limitadas e dependem do caminho que os fundos percorreram. Os casos em que os fundos são congelados ou devolvidos geralmente combinam três fatores: a vítima reagiu rápido, os fundos chegaram a uma exchange regulamentada ou a uma stablecoin que pode ser congelada, e uma denúncia oficial foi registrada. Quando nenhum desses fatores se aplica, a recuperação é rara.

É por isso que a prevenção importa tanto: uma vez que uma transação maliciosa é assinada, não existe botão de desfazer.

## Como Evitar da Próxima Vez

- Verifique o que você assina: a maioria dos roubos começa com uma aprovação, um permit ou uma transação que você não compreendeu totalmente. A Lumina Wallet analisa as transações antes que você as assine e alerta sobre riscos conhecidos, mas nenhuma ferramenta detecta tudo — e nenhuma carteira pode reverter uma transação depois.
- Instale carteiras apenas de fontes oficiais: veja [como verificar se o aplicativo de uma carteira cripto é verdadeiro](/pt/blog/verifier-app-wallet-crypto-authentique).
- Siga o [checklist de segurança para carteiras cripto](/pt/blog/checklist-securite-wallet-crypto-2026) para separar suas economias da carteira do dia a dia.

## Perguntas Frequentes

**Uma transação roubada na Ethereum pode ser revertida?**
Não. Transações confirmadas são definitivas. Os únicos caminhos realistas são o congelamento por uma exchange ou por um emissor de stablecoin, geralmente após uma denúncia oficial.

**Devo pagar a um serviço que promete recuperar minhas criptomoedas?**
Seja extremamente cauteloso. Ofertas não solicitadas, com taxas adiantadas ou resultados garantidos, são um golpe conhecido. Investigações de verdade passam pela polícia, pela exchange envolvida ou por um profissional habilitado e verificado.

**Vale a pena denunciar se o valor for pequeno?**
Sim. As denúncias ajudam os investigadores a ligar casos entre si, e as denúncias públicas em bancos de dados de golpes alertam outros usuários sobre os mesmos endereços.

**A Lumina Wallet pode recuperar meus fundos roubados?**
Não. A Lumina Wallet é non-custodial e não pode reverter nem congelar transações. Ela pode ajudar você a proteger uma nova carteira e a verificar transações futuras antes de assiná-las.

## Conclusão

Depois de um roubo de criptomoedas: proteja o que sobrou, guarde as provas, rastreie os fundos, denuncie pelos canais oficiais e recuse toda oferta paga de "recuperação" que chegar até você. Rapidez e denúncias oficiais são o que lhe dão a melhor chance — e não custam nada.`
  }
};

export default pt;
